import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, Package, Truck, Loader2, ChevronDown, Calendar, Check, XCircle, Edit3, ExternalLink, Receipt, AlertTriangle, Eye, Edit, Copy, FileDown, CheckCircle, Users, Building2, Phone, MapPin, FolderKanban, PoundSterling, Clock, PhoneCall, Hash, ArrowRightLeft } from 'lucide-react';
import { API } from '../App';
import api from '../services/api';
import { useNavigate, useLocation } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from './ui/dropdown-menu';
import { toast } from 'sonner';
import CallNoteModal from './CallNoteModal';
import { ContactNotesTasksModal } from './ContactNotesTasksModal';
import { ProjectManagementModal } from './ProjectManagementModal';
import { AddBarbourIdModal } from './AddBarbourIdModal';

const enconOrange = '#ee7203';
const tealTeal = '#0d9488';

// P.O. Type options for display
const PO_TYPES = [
  { value: 'branch_expense', label: 'Branch Expense (Ex Transport)' },
  { value: 'staff_cv_hire', label: 'Staff CV Hire' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'stationery', label: 'Stationery' },
  { value: 'other', label: 'Other' },
];

// Helper function to format date as "1st Jan 26"
const formatDateOrdinal = (dateStr) => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    
    const day = date.getDate();
    const month = date.toLocaleString('en-GB', { month: 'short' });
    const year = date.getFullYear().toString().slice(-2);
    
    // Get ordinal suffix
    const getOrdinalSuffix = (n) => {
      const s = ['th', 'st', 'nd', 'rd'];
      const v = n % 100;
      return s[(v - 20) % 10] || s[v] || s[0];
    };
    
    return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
  } catch (e) {
    return dateStr;
  }
};

// Helper function to format currency with commas
const formatCurrency = (amount, decimals = 2) => {
  const num = parseFloat(amount) || 0;
  return num.toLocaleString('en-GB', { 
    minimumFractionDigits: decimals, 
    maximumFractionDigits: decimals 
  });
};

// Helper to format project value
const formatProjectValue = (value) => {
  if (!value) return '—';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(num);
};

// Format date as "Mon 22nd Dec 25"
const formatTransportDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const date = typeof dateStr === 'string' ? parseISO(dateStr) : new Date(dateStr);
    const day = format(date, 'EEE');
    const dayNum = date.getDate();
    const month = format(date, 'MMM');
    const year = format(date, 'yy');
    
    const getOrdinal = (n) => {
      const s = ['th', 'st', 'nd', 'rd'];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };
    
    return `${day} ${getOrdinal(dayNum)} ${month} ${year}`;
  } catch (e) {
    return dateStr;
  }
};

const GlobalSearch = ({ onStockSelect, isMobile = false, user, dropdownTop = 124 }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [stockResults, setStockResults] = useState([]);
  const [transportResults, setTransportResults] = useState([]);
  const [goodsInResults, setGoodsInResults] = useState([]);
  const [expensesResults, setExpensesResults] = useState([]);
  const [errorLogResults, setErrorLogResults] = useState([]);
  const [customerResults, setCustomerResults] = useState([]);
  const [projectResults, setProjectResults] = useState([]);
  const [prefixResults, setPrefixResults] = useState([]);
  const [hasErrorLogPermission, setHasErrorLogPermission] = useState(false);
  const [hasCustomerPermission, setHasCustomerPermission] = useState(false);
  const [hasProjectPermission, setHasProjectPermission] = useState(false);
  const [upcomingTransport, setUpcomingTransport] = useState([]);
  const [pastTransport, setPastTransport] = useState([]);
  const [upcomingGoodsIn, setUpcomingGoodsIn] = useState([]);
  const [pastGoodsIn, setPastGoodsIn] = useState([]);
  const [matchedSupplierGroup, setMatchedSupplierGroup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);
  const [showStockModal, setShowStockModal] = useState(false);
  const [showInStockOnly, setShowInStockOnly] = useState(false); // CTRL toggle for in-stock only
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [selectedErrorLog, setSelectedErrorLog] = useState(null);
  const [showErrorLogModal, setShowErrorLogModal] = useState(false);
  const [errorLogForm, setErrorLogForm] = useState({});
  const [savingErrorLog, setSavingErrorLog] = useState(false);
  const [expandedProjectId, setExpandedProjectId] = useState(null);
  
  // Call Note Modal state
  const [showCallNoteModal, setShowCallNoteModal] = useState(false);
  const [selectedCustomerForCall, setSelectedCustomerForCall] = useState(null);
  
  // P.O. Modal states
  const [matchInvoiceMode, setMatchInvoiceMode] = useState(false);
  const [queryNotes, setQueryNotes] = useState('');
  
  // Expanded items state - now works for all devices
  const [expandedStockId, setExpandedStockId] = useState(null);
  const [expandedTransportId, setExpandedTransportId] = useState(null);
  const [expandedGoodsInId, setExpandedGoodsInId] = useState(null);
  const [expandedExpenseId, setExpandedExpenseId] = useState(null);
  const [expandedPrefixId, setExpandedPrefixId] = useState(null);
  
  // Past deliveries dropdown state
  const [pastDeliveriesExpanded, setPastDeliveriesExpanded] = useState(false);
  
  // Copy product code to clipboard
  const copyProductCode = async (code, e) => {
    if (e) e.stopPropagation();
    try {
      await navigator.clipboard.writeText(code);
      toast.success(`${code} copied to clipboard`);
    } catch (error) {
      toast.info(`Code: ${code}`);
    }
  };
  
  // Goods In Edit Modal
  const [showGoodsInEditModal, setShowGoodsInEditModal] = useState(false);
  const [editingGoodsIn, setEditingGoodsIn] = useState(null);
  const [goodsInForm, setGoodsInForm] = useState({
    date: '',
    supplier_name: '',
    order_number: '',
    notes: ''
  });
  
  // P.O. Expense Edit Modal
  const [showExpenseEditModal, setShowExpenseEditModal] = useState(false);
  const [expenseEditForm, setExpenseEditForm] = useState({
    supplier: '',
    order_date: '',
    ordered_by: '',
    customer_name: '',
    delivery_postcode: '',
    m4_order_no: '',
    notes: '',
    message_to_supplier: '',
    total_cost: 0,
    customer_recharge_amount: 0,
    ho_recharge_amount: 0,
    po_type: 'branch_expense',
  });
  const [savingExpenseEdit, setSavingExpenseEdit] = useState(false);
  
  const searchInputRef = useRef(null);
  const mobileInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  // Transport's own page background is a dark, full-bleed map — the default
  // black-on-glass search text becomes near-invisible there, so switch to
  // white text/icon (with a dark shadow instead of the usual light one) only
  // on that page.
  // The sub-header search bar is a light field on every page, Transport included (it used to be white text for Transport's dark
  // map, which left the typed text and the cursor invisible on the white bar), so its text and icons are always black.
  const isDarkBgPage = false;
  
  const debounceRef = useRef(null);
  // Bumped on EVERY change to the search box. A search captures the value when it starts and
  // throws its own results away if the box has changed since -- otherwise a slow reply for "347"
  // arriving after the reply for "3474669" overwrites it with the wide, wrong results.
  const searchSeqRef = useRef(0);
  const modeDebounceRef = useRef(null);
  
  // Check permissions
  const hasTransportPermission = user?.role === 'super_admin' || user?.can_edit_transport;
  const hasGoodsInPermission = true; // Anyone with access to search can view goods in
  const hasStockPermission = true; // Anyone can access stock

  // ── Customer / Project search-mode toggles (sub-header) ────────────────────
  // Explicit, opt-in CRM search — separate from the default free-text search
  // above (which never queries customer/project data, per GDPR). Only one mode
  // is active at a time.
  const [searchMode, setSearchMode] = useState('global'); // 'global' | 'customer' | 'project'
  const [modeResults, setModeResults] = useState([]);
  const [modeLoading, setModeLoading] = useState(false);
  const [customerModalAccount, setCustomerModalAccount] = useState(null);
  // An order-number search that matches a parsed order (a normal order OR a direct, which is not on Transport):
  // { id, order_number, customer_name, account_id, is_direct }. "Open Order" opens it in Customer Management.
  const [orderLookup, setOrderLookup] = useState(null);
  const [customerModalSearch, setCustomerModalSearch] = useState(null); // customer name, when the order has no CRM account id
  const [customerModalOrder, setCustomerModalOrder] = useState(null);   // { orderId, orderNumber } to open in the Orders tab
  const [projectModalId, setProjectModalId] = useState(null);
  const [addBarbourFor, setAddBarbourFor] = useState(null); // { id, name } — project missing a Barbour ID

  const setMode = (mode) => {
    setSearchMode(prev => (prev === mode ? 'global' : mode));
    // Keep whatever the user already typed — switching to Customer/Project
    // (or back to global) should re-run the search for that same term in
    // the new location, not clear the box. The existing debounced effects
    // on [searchQuery, searchMode] already re-fire the correct query.
    setIsOpen(!!searchQuery);
    setModeResults([]);
    setTimeout(() => searchInputRef.current?.focus(), 0);
  };

  const runModeSearch = async (q) => {
    setModeLoading(true);
    try {
      if (searchMode === 'customer') {
        const res = await api.get(`${API}/zoho/search-accounts`, { params: { q } });
        setModeResults(res.data?.accounts || []);
      } else if (searchMode === 'project') {
        const [projRes, acctRes] = await Promise.allSettled([
          api.get(`${API}/zoho/search-projects`, { params: { q } }),
          api.get(`${API}/zoho/search-accounts`, { params: { q } }),
        ]);
        const direct = (projRes.status === 'fulfilled' ? projRes.value.data?.projects : []) || [];
        const seen = new Set(direct.map(p => p.id));
        const merged = direct.map(p => ({
          id: p.id, name: p.deal_name || p.barbour_project_name, account_name: p.account_name,
          postcode: p.postcode, town: p.town, barbour_project_id: p.barbour_project_id,
        }));
        const accounts = (acctRes.status === 'fulfilled' ? acctRes.value.data?.accounts : []) || [];
        const topAccounts = accounts.slice(0, 3);
        if (topAccounts.length > 0) {
          const qpResults = await Promise.all(topAccounts.map(a =>
            api.get(`${API}/zoho/account/${a.id}/quotes-and-projects`)
              .then(r => ({ account: a, projects: r.data?.projects || [] }))
              .catch(() => ({ account: a, projects: [] }))
          ));
          qpResults.forEach(({ account, projects }) => {
            projects.forEach(p => {
              if (!seen.has(p.id)) {
                seen.add(p.id);
                merged.push({ id: p.id, name: p.name, account_name: account.name, _fromAccount: account.name });
              }
            });
          });
        }
        setModeResults(merged);
      }
    } catch (_) {
      setModeResults([]);
    } finally {
      setModeLoading(false);
    }
  };
  
  const performSearch = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setStockResults([]);
      setTransportResults([]);
      setGoodsInResults([]);
      setExpensesResults([]);
      setUpcomingTransport([]);
      setPastTransport([]);
      setUpcomingGoodsIn([]);
      setPastGoodsIn([]);
      setMatchedSupplierGroup(null);
      setErrorLogResults([]);
      setCustomerResults([]);
      setProjectResults([]);
      setPrefixResults([]);
      setOrderLookup(null);
      return;
    }
    
    setLoading(true);
    const mySeq = searchSeqRef.current;
    const token = localStorage.getItem('token');
    
    try {
      // Customer and Projects search removed per GDPR requirements
      const [stockRes, transportRes, goodsInRes, expensesRes, errorLogRes, prefixRes] = await Promise.all([
        api.get(`${API}/stock-database/search`, {
          params: { q: query, limit: 100, include_all: true },
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { results: [] } })),
        api.get(`${API}/transport/search`, {
          params: { q: query, limit: 40 },
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { results: [], upcoming: [], past: [] } })),
        api.get(`${API}/goods-in/search`, {
          params: { q: query, limit: 20 },
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { results: [], upcoming: [], past: [] } })),
        api.get(`${API}/po-expenses/search`, {
          params: { query: query },
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { expenses: [] } })),
        api.get(`${API}/error-log/search`, {
          params: { q: query, limit: 15 },
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { results: [], has_permission: false } })),
        api.get(`${API}/stock-database/prefix-search`, {
          params: { q: query },
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { prefixes: [] } }))
      ]);
      
      if (mySeq !== searchSeqRef.current) return; // a newer keystroke superseded this search
      setStockResults(stockRes.data.results || []);
      setMatchedSupplierGroup(stockRes.data.matched_supplier_group || null);
      setTransportResults(transportRes.data.results || []);
      setUpcomingTransport(transportRes.data.upcoming || []);
      setPastTransport(transportRes.data.past || []);
      setGoodsInResults(goodsInRes.data.results || []);
      setUpcomingGoodsIn(goodsInRes.data.upcoming || []);
      setPastGoodsIn(goodsInRes.data.past || []);
      setExpensesResults(expensesRes.data.expenses || []);
      setErrorLogResults(errorLogRes.data.results || []);
      setHasErrorLogPermission(errorLogRes.data.has_permission !== false);
      setPrefixResults(prefixRes.data.prefixes || []);
      // Customer and Projects search removed - always empty
      setCustomerResults([]);
      setHasCustomerPermission(false);
      setProjectResults([]);
      setHasProjectPermission(false);

      // A search for an order number (e.g. 3475093) also looks the order itself up, so it can be opened in Customer
      // Management — this finds directs too, which are never on Transport.
      setOrderLookup(null);
      const orderDigits = String(query || '').trim().replace(/^#/, '');
      if (/^\d{5,}$/.test(orderDigits)) {
        api.get(`${API}/customer-pricing/find-order`, {
          params: { order_number: orderDigits },
          headers: { Authorization: `Bearer ${token}` }
        }).then((r) => {
          if (mySeq === searchSeqRef.current && r.data?.found) setOrderLookup(r.data.order);
        }).catch(() => {});
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      if (mySeq === searchSeqRef.current) setLoading(false);
    }
  }, []);
  
  // Handle error log quick edit
  const handleErrorLogEdit = (entry) => {
    setSelectedErrorLog(entry);
    setErrorLogForm({
      order_number: entry.order_number || '',
      customer_name: entry.customer_name || '',
      names: entry.names || [],
      date: entry.date || '',
      what_happened: entry.what_happened || '',
      resolution: entry.resolution || '',
      status: entry.status || 'active',
      error_types: entry.error_types || [],
      cost_to_company: entry.cost_to_company || 0,
      cost_recovered: entry.cost_recovered || 0
    });
    setShowErrorLogModal(true);
  };
  
  // Save error log edit
  const handleSaveErrorLog = async () => {
    if (!selectedErrorLog) return;
    
    setSavingErrorLog(true);
    try {
      const token = localStorage.getItem('token');
      await api.put(`${API}/error-log/entry/${selectedErrorLog.id}`, {
        ...errorLogForm,
        financial_year_id: selectedErrorLog.financial_year_id,
        month_number: selectedErrorLog.month_number
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Error log entry updated');
      setShowErrorLogModal(false);
      
      // Refresh search results
      if (searchQuery) {
        performSearch(searchQuery);
      }
    } catch (error) {
      console.error('Error saving error log:', error);
      toast.error('Failed to save error log entry');
    } finally {
      setSavingErrorLog(false);
    }
  };
  
  // Navigate to error log entry and highlight
  const handleViewErrorLog = (entry) => {
    setIsOpen(false);
    // Navigate with query params for highlight
    navigate(`/error-log?year=${entry.financial_year_id}&month=${entry.month_number}&highlight=${entry.id}`);
  };
  
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    searchSeqRef.current += 1;
    if (searchMode !== 'global') return; // Customer/Project modes use their own debounce below

    if (searchQuery) {
      debounceRef.current = setTimeout(() => {
        performSearch(searchQuery);
      }, 350);
    } else {
      setStockResults([]);
      setTransportResults([]);
      setGoodsInResults([]);
      setExpensesResults([]);
      setUpcomingTransport([]);
      setPastTransport([]);
      setUpcomingGoodsIn([]);
      setPastGoodsIn([]);
      setMatchedSupplierGroup(null);
    }
    
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchQuery, performSearch, searchMode]);

  // Customer / Project mode debounced search — only fires while a toggle is active.
  useEffect(() => {
    if (modeDebounceRef.current) clearTimeout(modeDebounceRef.current);
    if (searchMode === 'global') { setModeResults([]); return; }
    if (!searchQuery || searchQuery.trim().length < 2) { setModeResults([]); return; }
    modeDebounceRef.current = setTimeout(() => runModeSearch(searchQuery.trim()), 250);
    return () => { if (modeDebounceRef.current) clearTimeout(modeDebounceRef.current); };
  }, [searchQuery, searchMode]); // eslint-disable-line
  
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);
  
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
        setSearchQuery('');
        setSearchMode('global');
        resetExpanded();
      }
      // CTRL key toggles "show in-stock only" filter for stock results
      if (e.key === 'Control' && isOpen && stockResults.length > 0) {
        setShowInStockOnly(prev => !prev);
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, stockResults.length]);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideDropdown = !dropdownRef.current || !dropdownRef.current.contains(event.target);
      if (isOutsideDropdown) {
        if (event.target.closest('[data-search-trigger]')) {
          return;
        }
        // Don't close if clicking on error log edit button or modal
        if (event.target.closest('[data-errorlog-edit]') || event.target.closest('[data-errorlog-modal]')) {
          return;
        }
        // Don't close if clicking inside a branch-transfer dropdown menu (Radix
        // portals this to document.body, outside dropdownRef, so it would
        // otherwise look like an "outside" click and close the whole search).
        if (event.target.closest('[data-branch-transfer-menu]')) {
          return;
        }
        // Don't close if modal is already open
        if (showErrorLogModal) {
          return;
        }
        setIsOpen(false);
        // Clicking away from the search box also turns the Customer/Project
        // toggle back off, per user request.
        setSearchMode('global');
      }
    };
    
    if (isOpen || searchMode !== 'global') {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, showErrorLogModal, searchMode]);
  
  const resetExpanded = () => {
    setExpandedStockId(null);
    setExpandedTransportId(null);
    setExpandedGoodsInId(null);
    setExpandedExpenseId(null);
    setExpandedPrefixId(null);
    setPastDeliveriesExpanded(false);
  };
  
  const toggleSearch = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchQuery('');
      setStockResults([]);
      setTransportResults([]);
      setGoodsInResults([]);
      setExpensesResults([]);
      resetExpanded();
    }
  };
  
  // Handle expense item click - open full P.O. modal
  const handleExpenseClick = (expense) => {
    setSelectedExpense(expense);
    setMatchInvoiceMode(false);
    setQueryNotes(expense.query_notes || '');
    setShowExpenseModal(true);
  };
  
  // Navigate to edit expense page
  const handleEditExpense = (expense) => {
    setExpenseEditForm({
      supplier: expense.supplier || '',
      order_date: expense.order_date || '',
      ordered_by: expense.ordered_by || '',
      customer_name: expense.customer_name || '',
      delivery_postcode: expense.delivery_postcode || '',
      m4_order_no: expense.m4_order_no || '',
      notes: expense.notes || '',
      message_to_supplier: expense.message_to_supplier || '',
      total_cost: expense.total_cost || 0,
      customer_recharge_amount: expense.customer_recharge_amount || 0,
      ho_recharge_amount: expense.ho_recharge_amount || 0,
      po_type: expense.po_type || 'branch_expense',
    });
    setShowExpenseModal(false);
    setShowExpenseEditModal(true);
  };
  
  // Save expense edit
  const handleSaveExpenseEdit = async () => {
    if (!selectedExpense) return;
    
    setSavingExpenseEdit(true);
    try {
      const token = localStorage.getItem('token');
      await api.put(`${API}/po-expenses/${selectedExpense.id}`, expenseEditForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('P.O. updated successfully');
      setShowExpenseEditModal(false);
      
      // Refresh search results
      if (searchQuery) {
        performSearch(searchQuery);
      }
    } catch (error) {
      console.error('Error saving expense:', error);
      toast.error('Failed to update P.O.');
    } finally {
      setSavingExpenseEdit(false);
    }
  };
  
  // Cancel P.O. from modal - exact match to POExpenses
  const handleCancelPO = async (expense) => {
    if (!window.confirm('Mark this P.O. as cancelled? The number will show as unused.')) return;
    
    try {
      const token = localStorage.getItem('token');
      await api.put(`${API}/po-expenses/${expense.id}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('P.O. marked as cancelled');
      setSelectedExpense({ ...expense, status: 'cancelled' });
      performSearch(searchQuery); // Refresh results
    } catch (error) {
      console.error('Error cancelling P.O.:', error);
      toast.error('Failed to cancel P.O.');
    }
  };
  
  // Uncancel P.O. from modal - exact match to POExpenses
  const handleUncancelPO = async (expense) => {
    if (!window.confirm('Restore this cancelled P.O.?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await api.put(`${API}/po-expenses/${expense.id}`, { status: 'active' }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('P.O. restored successfully');
      setSelectedExpense({ ...expense, status: 'active' });
      performSearch(searchQuery); // Refresh results
    } catch (error) {
      console.error('Error restoring P.O.:', error);
      toast.error('Failed to restore P.O.');
    }
  };
  
  // Generate PDF for P.O. - exact match to POExpenses
  const generatePOPDF = async (expense) => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.post(`${API}/po-expenses/${expense.id}/generate-pdf`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PO_${expense.po_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF generated successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };
  
  // Copy P.O. number to clipboard - exact match to POExpenses
  const copyPONumber = async (poNumber) => {
    try {
      await navigator.clipboard.writeText(poNumber);
      toast.success(`${poNumber} copied to clipboard`);
    } catch (error) {
      console.log('Clipboard not available');
      toast.info(`P.O. Number: ${poNumber}`);
    }
  };
  
  // Toggle match invoice mode - exact match to POExpenses
  const toggleMatchInvoiceMode = () => {
    setQueryNotes(selectedExpense?.query_notes || '');
    setMatchInvoiceMode(!matchInvoiceMode);
  };
  
  // Save query notes - exact match to POExpenses
  const saveQueryNotes = async () => {
    if (!selectedExpense) return;
    
    try {
      const token = localStorage.getItem('token');
      await api.put(`${API}/po-expenses/${selectedExpense.id}`, {
        query_notes: queryNotes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Query notes saved');
      setMatchInvoiceMode(false);
      setQueryNotes('');
      setShowExpenseModal(false);
      performSearch(searchQuery); // Refresh results
    } catch (error) {
      console.error('Error saving query notes:', error);
      toast.error('Failed to save query notes');
    }
  };
  
  // Mark P.O. as matched - exact match to POExpenses
  const markAsMatched = async () => {
    if (!selectedExpense) return;
    
    try {
      const token = localStorage.getItem('token');
      await api.put(`${API}/po-expenses/${selectedExpense.id}`, {
        invoice_matched: true,
        matched_at: new Date().toISOString(),
        query_notes: queryNotes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Invoice marked as matched');
      setMatchInvoiceMode(false);
      setQueryNotes('');
      setShowExpenseModal(false);
      performSearch(searchQuery); // Refresh results
    } catch (error) {
      console.error('Error marking as matched:', error);
      toast.error('Failed to mark as matched');
    }
  };
  
  // Open a new Stock Transfer request FROM the clicked branch INTO the signed-in
  // branch, with this product pre-added. Reads back out in StockTransfer.jsx.
  const handleTransferToBranch = (item, ob) => {
    setIsOpen(false);
    setSearchQuery('');
    sessionStorage.setItem('stock_transfer_prefill', JSON.stringify({
      branchCode: ob.branch_code,
      branchName: ob.branch_name,
      product_code: item.product_code,
      description: item.description,
      stock_unit: item.stock_unit || ob.stock_unit,
      weight: item.weight || 0,
      timestamp: Date.now()
    }));
    navigate('/stock-transfer');
  };

  // Handle stock item click - expand dropdown
  const handleStockClick = (stock, idx) => {
    if (expandedStockId === idx) {
      setExpandedStockId(null);
    } else {
      setExpandedStockId(idx);
      setExpandedTransportId(null);
      setExpandedGoodsInId(null);
      setExpandedExpenseId(null);
    }
  };
  
  // Open stock modal from dropdown
  const openStockModal = (stock) => {
    setSelectedStock(stock);
    setShowStockModal(true);
  };
  
  // Handle transport item click - expand dropdown
  const handleTransportClick = (entry, idx) => {
    if (expandedTransportId === idx) {
      setExpandedTransportId(null);
    } else {
      setExpandedTransportId(idx);
      setExpandedStockId(null);
      setExpandedGoodsInId(null);
    }
  };
  
  // Navigate to transport page (only if has permission)
  const navigateToTransport = (entry) => {
    if (!hasTransportPermission) {
      toast.error('You do not have permission to access Transport');
      return;
    }
    
    const date = entry.date || entry.collection_date;
    if (date) {
      setIsOpen(false);
      setSearchQuery('');
      const highlightData = {
        date: date,
        vehicleId: entry.vehicle_id,
        vehicleIndex: entry.vehicle_index,
        entryIndex: entry.entry_index,
        orderId: entry.order_number || entry.id,
        timestamp: Date.now()
      };
      sessionStorage.setItem('transport_highlight', JSON.stringify(highlightData));
      navigate(`/transport?date=${date}&viewMode=true&highlightOrder=${entry.order_number || entry.id}`);
    }
  };
  
  // "Open Order": closes the search and opens Customer Management on this order's customer with the Orders tab selected and
  // this exact order open. An order whose customer has no CRM account id opens by customer name instead.
  const openOrderInCustomerManagement = (order) => {
    if (!order) return;
    setIsOpen(false);
    setSearchQuery('');
    setCustomerModalOrder({ orderId: order.id, orderNumber: order.order_number });
    if (order.account_id) {
      setCustomerModalSearch(null);
      setCustomerModalAccount({ id: order.account_id, name: order.customer_name });
    } else {
      setCustomerModalAccount(null);
      setCustomerModalSearch(order.customer_name || null);
    }
  };

  // The blue "Open Order" button that sits next to "View on Transport" on the transport result for the order found.
  const renderOpenOrderBtn = (item) => {
    if (!orderLookup) return null;
    const numbers = [item?.order_number, ...(item?.order_numbers || [])].map((n) => String(n || '').replace(/^#/, '').trim());
    if (!numbers.includes(String(orderLookup.order_number))) return null;
    return (
      <button
        onClick={(e) => { e.stopPropagation(); openOrderInCustomerManagement(orderLookup); }}
        className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white flex items-center gap-1.5 transition-colors hover:opacity-90"
        style={{ backgroundColor: '#2563eb' }}
        title="Open this order in Customer Management"
        data-testid={`search-open-order-${orderLookup.order_number}`}
      >
        <Package className="w-3.5 h-3.5" />
        Open Order
      </button>
    );
  };

  // The order found by the search when it is NOT on Transport (for example a direct order): its own result card.
  const renderOrderOnlyCard = () => {
    if (!orderLookup || !(activeTab === 'all' || activeTab === 'transport')) return null;
    const onTransport = [...upcomingTransport, ...pastTransport].some((item) =>
      [item?.order_number, ...(item?.order_numbers || [])].map((n) => String(n || '').replace(/^#/, '').trim()).includes(String(orderLookup.order_number)));
    if (onTransport) return null;
    return (
      <div className="p-3" data-testid="search-order-only-card">
        <div className="flex items-center gap-2 mb-3 px-2">
          <Package className="w-4 h-4" style={{ color: '#60a5fa' }} />
          <span className="text-sm font-medium" style={{ color: '#60a5fa' }}>Order</span>
        </div>
        <div className="rounded-lg overflow-hidden bg-white/5 hover:bg-white/10 border-l-2 border-blue-500 p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-wrap flex-1">
              <span className="px-2 py-0.5 rounded text-xs font-bold bg-blue-600 text-white">#{orderLookup.order_number}</span>
              <span className="font-semibold text-white">{orderLookup.customer_name || 'Unknown'}</span>
              {orderLookup.is_direct && (
                <span className="px-2 py-0.5 rounded text-xs font-semibold bg-blue-500/30 text-blue-200">Direct — not on Transport</span>
              )}
            </div>
            <div className="flex items-center gap-3 ml-4">
              <button
                onClick={(e) => { e.stopPropagation(); openOrderInCustomerManagement(orderLookup); }}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white flex items-center gap-1.5 transition-colors hover:opacity-90"
                style={{ backgroundColor: '#2563eb' }}
                data-testid={`search-open-order-${orderLookup.order_number}`}
              >
                <Package className="w-3.5 h-3.5" />
                Open Order
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Handle Enter key press - navigate to top transport result if searching order number
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      // Check if we have transport results
      const topTransportResult = upcomingTransport[0] || pastTransport[0];
      
      if (topTransportResult && hasTransportPermission) {
        // Navigate to the top transport result
        navigateToTransport(topTransportResult);
      }
    }
  };
  
  // Handle goods in item click - expand dropdown
  const handleGoodsInClick = (entry, idx) => {
    if (expandedGoodsInId === idx) {
      setExpandedGoodsInId(null);
    } else {
      setExpandedGoodsInId(idx);
      setExpandedStockId(null);
      setExpandedTransportId(null);
    }
  };
  
  // Open goods in edit modal (only if has permission)
  const openGoodsInEditModal = (entry) => {
    if (!hasGoodsInPermission) {
      toast.error('You do not have permission to edit Goods In');
      return;
    }
    
    setEditingGoodsIn(entry);
    setGoodsInForm({
      date: entry.date,
      supplier_name: entry.supplier_name,
      order_number: entry.order_number,
      notes: entry.notes || ''
    });
    setShowGoodsInEditModal(true);
  };
  
  // Update goods in entry
  const handleUpdateGoodsIn = async () => {
    if (!goodsInForm.supplier_name || !goodsInForm.order_number) {
      toast.error('Please fill in supplier name and order number');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await api.put(`${API}/goods-in/deliveries/${editingGoodsIn.id}`, goodsInForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Delivery updated successfully');
      setShowGoodsInEditModal(false);
      setEditingGoodsIn(null);
      performSearch(searchQuery); // Refresh results
    } catch (error) {
      console.error('Failed to update delivery:', error);
      toast.error('Failed to update delivery');
    }
  };
  
  // Get filtered results based on active tab
  const getFilteredResults = () => {
    // Helper to check if stock item has stock or stock on order
    const hasStockOrOnOrder = (item) => {
      const inStock = item.on_hand_quantity || item.on_hand_balance || item.free_stock || 0;
      const onOrder = item.on_order_quantity || 0;
      return inStock > 0 || onOrder > 0;
    };
    
    // Filter stock based on showInStockOnly toggle
    const getStockResults = () => {
      if (showInStockOnly) {
        return stockResults.filter(hasStockOrOnOrder);
      }
      return stockResults;
    };
    
    switch (activeTab) {
      case 'stock':
        return { stock: getStockResults(), transport: [], goodsIn: [], expenses: [], errorLog: [] };
      case 'transport':
        return { stock: [], transport: transportResults, goodsIn: [], expenses: [], errorLog: [] };
      case 'goodsin':
        return { stock: [], transport: [], goodsIn: goodsInResults, expenses: [], errorLog: [] };
      case 'expenses':
        return { stock: [], transport: [], goodsIn: [], expenses: expensesResults, errorLog: [] };
      case 'errorlog':
        return { stock: [], transport: [], goodsIn: [], expenses: [], errorLog: errorLogResults };
      default:
        // Customer and Projects search removed per GDPR requirements
        return { stock: getStockResults(), transport: transportResults, goodsIn: goodsInResults, expenses: expensesResults, errorLog: hasErrorLogPermission ? errorLogResults : [] };
    }
  };
  
  // Helper to check if item is out of stock
  const isOutOfStock = (item) => {
    const inStock = item.on_hand_quantity || item.on_hand_balance || item.free_stock || 0;
    const onOrder = item.on_order_quantity || 0;
    return inStock <= 0 && onOrder <= 0;
  };
  
  const filtered = getFilteredResults();
  const hasResults = filtered.stock.length > 0 || filtered.transport.length > 0 || filtered.goodsIn.length > 0 || filtered.expenses.length > 0 || filtered.errorLog.length > 0 || prefixResults.length > 0;
  const totalResults = filtered.stock.length + filtered.transport.length + filtered.goodsIn.length + filtered.expenses.length + filtered.errorLog.length;

  // Check if search query is primarily numeric (for order number searches)
  const isNumericSearch = /^\d+$/.test(searchQuery.trim()) || /^\d{3,}/.test(searchQuery.trim());
  
  // Check if search query is a P.O. number search (starts with 30X)
  const isPONumberSearch = searchQuery.trim().toUpperCase().startsWith('30X');
  
  // Determine if Goods In should be shown first (when searching numbers and has results)
  const showGoodsInFirst = isNumericSearch && goodsInResults.length > 0 && !isPONumberSearch;
  
  // Determine if Expenses should be shown first (when searching 30X and has expense results)
  const showExpensesFirst = isPONumberSearch && expensesResults.length > 0;

  // Get status color for goods in
  const getStatusColor = (status) => {
    if (status === 'complete') return '#22c55e';
    if (status === 'failed') return '#ef4444';
    return enconOrange;
  };

  // Render expanded stock details
  const renderStockDetails = (item, canOpenModal = true) => (
    <div className="bg-white/10 p-3 animate-slideDown border-t border-white/10">
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="bg-green-500/20 rounded-lg p-2 text-center">
          <span className="text-[10px] text-green-300 block">In Stock</span>
          <span className="text-lg font-bold text-green-400">{item.on_hand_quantity || item.on_hand_balance || 0}</span>
          <span className="text-[10px] text-green-300 ml-1">{item.stock_unit || ''}</span>
        </div>
        <div className="bg-yellow-500/20 rounded-lg p-2 text-center">
          <span className="text-[10px] text-yellow-300 block">On Order</span>
          <span className="text-lg font-bold text-yellow-400">{item.on_order_quantity || item.on_order || 0}</span>
          <span className="text-[10px] text-yellow-300 ml-1">{item.stock_unit || ''}</span>
        </div>
        <div className="bg-blue-500/20 rounded-lg p-2 text-center">
          <span className="text-[10px] text-blue-300 block">Total Stock</span>
          <span className="text-lg font-bold text-blue-400">{item.free_stock || item.on_hand_quantity || item.on_hand_balance || 0}</span>
          <span className="text-[10px] text-blue-300 ml-1">{item.stock_unit || ''}</span>
        </div>
      </div>
      
      {/* GDPR: Unit Cost and Total Value removed from product display */}
      
      {/* Product Weight */}
      {item.weight > 0 && (
        <div className="bg-purple-500/20 rounded-lg p-2 mb-3 flex items-center justify-between">
          <span className="text-[10px] text-purple-300">Weight (kg)</span>
          <span className="text-sm font-bold text-purple-400">{item.weight.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </div>
      )}
      
      <div className="space-y-1.5 text-xs">
        {item.supplier_group && (
          <div className="flex justify-between">
            <span className="text-gray-400">Supplier Group</span>
            <span className="text-white">{item.supplier_group}</span>
          </div>
        )}
        {item.analysis_group && (
          <div className="flex justify-between">
            <span className="text-gray-400">Analysis Group</span>
            <span className="text-white">{item.analysis_group}</span>
          </div>
        )}
        {item.category && (
          <div className="flex justify-between">
            <span className="text-gray-400">Category</span>
            <span className="text-white">{item.category}</span>
          </div>
        )}
        {item.location && (
          <div className="flex justify-between">
            <span className="text-gray-400">Location</span>
            <span className="text-white">{item.location}</span>
          </div>
        )}
      </div>
      
      {canOpenModal && (
        <button
          onClick={(e) => { e.stopPropagation(); openStockModal(item); }}
          className="mt-3 w-full py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
          style={{ backgroundColor: enconOrange, color: 'white' }}
        >
          <ExternalLink className="w-4 h-4" />
          Open Full Details
        </button>
      )}
    </div>
  );

  // Render expanded transport details
  const renderTransportDetails = (item, canNavigate = true) => (
    <div className="bg-white/10 p-3 animate-slideDown border-t border-white/10">
      <div className="space-y-2 text-xs">
        <div className="flex justify-between">
          <span className="text-gray-400">Order Number</span>
          <span className="text-white font-semibold">#{item.order_number || item.id}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-400">Customer</span>
          <span className="text-white">{item.customer_name || 'Unknown'}</span>
        </div>
        {item.delivery_name && item.delivery_name !== item.customer_name && (
          <div className="flex justify-between">
            <span className="text-gray-400">Delivery Name</span>
            <span className="text-white">{item.delivery_name}</span>
          </div>
        )}
        {item.postcode && (
          <div className="flex justify-between">
            <span className="text-gray-400">Postcode</span>
            <span className="text-white">{item.postcode}</span>
          </div>
        )}
        <div className="flex justify-between">
          <span className="text-gray-400">Date</span>
          <span className="text-white">{formatTransportDate(item.date || item.collection_date)}</span>
        </div>
        {item.status && (
          <div className="flex justify-between">
            <span className="text-gray-400">Status</span>
            <span className={`font-semibold ${
              item.status === 'delivered' ? 'text-green-400' :
              item.status === 'pending' ? 'text-yellow-400' : 'text-gray-400'
            }`}>{item.status}</span>
          </div>
        )}
      </div>
      
      {canNavigate && hasTransportPermission ? (
        <button
          onClick={(e) => { e.stopPropagation(); navigateToTransport(item); }}
          className="mt-3 w-full py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
          style={{ backgroundColor: enconOrange, color: 'white' }}
        >
          <ExternalLink className="w-4 h-4" />
          Go to Transport View
        </button>
      ) : !hasTransportPermission && (
        <div className="mt-3 w-full py-2 rounded-lg text-sm text-center text-gray-400 bg-white/5">
          Transport permission required to navigate
        </div>
      )}
    </div>
  );

  // Render expanded goods in details
  const renderGoodsInDetails = (item, canEdit = true) => (
    <div className="bg-white/10 p-3 animate-slideDown border-t border-white/10">
      <div className="space-y-2 text-xs">
        <div className="flex justify-between">
          <span className="text-gray-400">Order Number</span>
          <span className="text-white font-semibold">#{item.order_number}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-400">Supplier</span>
          <span className="text-white">{item.supplier_name}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-400">Date</span>
          <span className="text-white">{formatTransportDate(item.date)}</span>
        </div>
        {item.status && (
          <div className="flex justify-between items-center">
            <span className="text-gray-400">Status</span>
            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
              item.status === 'complete' ? 'bg-green-500 text-white' :
              item.status === 'failed' ? 'bg-red-500 text-white' : ''
            }`}>
              {item.status === 'complete' ? '✓ Complete' : item.status === 'failed' ? '✗ Failed' : item.status}
            </span>
          </div>
        )}
        {item.notes && (
          <div className="pt-2 border-t border-white/10 mt-2">
            <span className="text-gray-400 block mb-1">Notes</span>
            <p className="text-white whitespace-pre-line text-[11px] bg-white/5 rounded p-2">
              {item.notes}
            </p>
          </div>
        )}
      </div>
      
      {canEdit && hasGoodsInPermission ? (
        <button
          onClick={(e) => { e.stopPropagation(); openGoodsInEditModal(item); }}
          className="mt-3 w-full py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
          style={{ backgroundColor: enconOrange, color: 'white' }}
        >
          <Edit3 className="w-4 h-4" />
          Quick Edit
        </button>
      ) : !hasGoodsInPermission && (
        <div className="mt-3 w-full py-2 rounded-lg text-sm text-center text-gray-400 bg-white/5">
          Goods In permission required to edit
        </div>
      )}
    </div>
  );

  // Mobile: Shortened search bar with slide-down results
  if (isMobile) {
    return (
      <>
        <div className="relative flex-1 mx-1">
          <input
            ref={mobileInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              if (e.target.value) setIsOpen(true);
              resetExpanded();
            }}
            onFocus={() => {
              if (searchQuery) setIsOpen(true);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Search..."
            className={`w-full pl-7 pr-2 py-1.5 rounded-lg border-0 focus:ring-2 focus:ring-orange-400 ${
              isOpen ? 'bg-gray-300' : 'bg-gray-100'
            }`}
            style={{ fontSize: '16px' }}
          />
          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
        </div>

        {isOpen && searchQuery && createPortal(
          <div 
            className="fixed left-0 right-0 z-[55] animate-slideDown"
            style={{ top: '60px' }}
          >
            <div 
              className="w-full max-h-[65vh] overflow-y-auto"
              style={{
                background: 'rgba(50, 50, 50, 0.97)',
                backdropFilter: 'blur(10px)',
                borderBottom: '2px solid rgba(238, 114, 3, 0.5)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
              }}
            >
              {/* Tabs - Customer and Projects tabs removed per GDPR requirements */}
              <div className="sticky top-0 flex gap-1 p-2 border-b border-white/10 overflow-x-auto" style={{ background: 'rgba(50, 50, 50, 0.98)' }}>
                {['all', 'prefix', 'stock', 'transport', 'goodsin', 'expenses', ...(hasErrorLogPermission ? ['errorlog'] : [])].map((tab) => {
                  // Get result count for each tab
                  const getTabCount = () => {
                    switch(tab) {
                      case 'all': return totalResults;
                      case 'prefix': return prefixResults.length;
                      case 'stock': return stockResults.length;
                      case 'transport': return transportResults.length;
                      case 'goodsin': return goodsInResults.length;
                      case 'expenses': return expensesResults.length;
                      case 'errorlog': return errorLogResults.length;
                      default: return 0;
                    }
                  };
                  // Get tab color based on type
                  const getTabColor = () => {
                    switch(tab) {
                      case 'all': return enconOrange;
                      case 'prefix': return '#8b5cf6'; // purple
                      case 'stock': return '#22c55e';
                      case 'transport': return '#22c55e';
                      case 'goodsin': return '#22c55e';
                      case 'expenses': return '#3b82f6';
                      case 'errorlog': return '#ef4444';
                      default: return enconOrange;
                    }
                  };
                  const count = getTabCount();
                  const hasResults = count > 0;
                  const tabColor = getTabColor();
                  const isActive = activeTab === tab;
                  
                  return (
                    <button
                      key={tab}
                      onClick={() => { setActiveTab(tab); resetExpanded(); }}
                      className={`px-2 py-1.5 rounded-lg text-[10px] font-medium whitespace-nowrap flex items-center gap-1 ${
                        isActive ? 'text-white' : hasResults ? 'text-white' : 'text-gray-400'
                      }`}
                      style={{ 
                        background: isActive ? tabColor : 'rgba(255,255,255,0.1)',
                        border: hasResults && !isActive ? `1.5px solid ${tabColor}` : 'none'
                      }}
                    >
                      <span>{tab === 'all' ? 'All' : 
                             tab === 'prefix' ? 'Prefix' :
                             tab === 'stock' ? 'Stock' : 
                             tab === 'transport' ? 'Transport' :
                             tab === 'expenses' ? 'Expenses' :
                             tab === 'errorlog' ? 'Errors' :
                             tab === 'customers' ? 'Customers' :
                             tab === 'projects' ? 'Projects' :
                             'Goods In'}</span>
                      {hasResults && (
                        <span 
                          className="px-1 py-0.5 rounded-full text-[9px] font-bold"
                          style={{ 
                            backgroundColor: isActive ? 'rgba(255,255,255,0.3)' : tabColor,
                            color: 'white'
                          }}
                        >
                          {count}
                        </span>
                      )}
                    </button>
                  );
                })}
                <button
                  onClick={() => { setIsOpen(false); setSearchQuery(''); resetExpanded(); }}
                  className="ml-auto px-2 py-1 rounded-lg text-gray-400 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Results */}
              <div className="p-2">
                {loading ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                  </div>
                ) : !hasResults ? (
                  <div className="text-center py-6 text-gray-400 text-sm">
                    No results found
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {/* P.O. Expenses Results - Show FIRST when searching 30X */}
                    {showExpensesFirst && (activeTab === 'all' || activeTab === 'expenses') && (
                      <>
                        <div className="flex items-center gap-1.5 px-1 py-1.5">
                          <Receipt className="w-3 h-3" style={{ color: '#3b82f6' }} />
                          <span className="text-xs font-medium" style={{ color: '#3b82f6' }}>P.O. Expenses ({filtered.expenses.length})</span>
                        </div>
                        {filtered.expenses.map((expense) => (
                          <div 
                            key={`exp-first-${expense.id}`}
                            className="rounded-lg overflow-hidden bg-white/5 hover:bg-white/10 border-l-2 border-blue-500 mb-1.5 cursor-pointer"
                            onClick={() => handleExpenseClick(expense)}
                          >
                            <div className="p-2.5">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold text-white bg-blue-600">
                                  {expense.po_number}
                                </span>
                                <span className="font-medium text-sm text-white flex-1 truncate">{expense.supplier || 'No Supplier'}</span>
                                <span className="text-xs text-green-400 font-semibold">£{(expense.total_cost || 0).toFixed(2)}</span>
                              </div>
                              <div className="flex items-center justify-between mt-1 flex-wrap gap-1">
                                <div className="flex items-center gap-2 text-[10px] text-gray-400">
                                  <span>{expense.order_date}</span>
                                  {expense.po_type && <span className="capitalize">• {expense.po_type.replace(/_/g, ' ')}</span>}
                                </div>
                                <div className="flex items-center gap-1">
                                  {expense.linked_transfer_id && (
                                    <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-purple-500 text-white">Linked</span>
                                  )}
                                  {expense.status === 'cancelled' && (
                                    <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-500 text-white">Cancelled</span>
                                  )}
                                  <button
                                    className="px-2 py-0.5 rounded text-[10px] font-bold text-white flex items-center gap-1"
                                    style={{ backgroundColor: enconOrange }}
                                    onClick={(e) => { e.stopPropagation(); handleExpenseClick(expense); }}
                                  >
                                    <ExternalLink className="w-3 h-3" />
                                    Open P.O.
                                  </button>
                                </div>
                              </div>
                              {expense.customer_recharge_amount > 0 && (
                                <p className="text-[10px] text-blue-300 mt-0.5">Recharge: £{expense.customer_recharge_amount.toFixed(2)}</p>
                              )}
                              {expense.m4_order_no && (
                                <p className="text-[10px] text-gray-500 mt-0.5">M4: {expense.m4_order_no}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                    
                    {/* Prefix Results - ONLY show when prefix tab is explicitly selected */}
                    {prefixResults.length > 0 && activeTab === 'prefix' && (
                      <>
                        <div className="flex items-center gap-1.5 px-1 py-1.5">
                          <Hash className="w-3 h-3" style={{ color: '#8b5cf6' }} />
                          <span className="text-xs font-medium" style={{ color: '#8b5cf6' }}>Prefix Codes ({prefixResults.length})</span>
                        </div>
                        <div className="space-y-1">
                          {prefixResults.map((prefixGroup, prefixIdx) => (
                            <div 
                              key={`prefix-${prefixGroup.prefix}`}
                              className="rounded-lg overflow-hidden"
                            >
                              {/* Prefix Row - Main clickable area */}
                              <div 
                                className={`flex items-center justify-between p-2 transition-colors ${expandedPrefixId === prefixIdx ? 'bg-purple-900/30' : 'bg-white/5 hover:bg-white/10'}`}
                              >
                                <button
                                  onClick={() => setExpandedPrefixId(expandedPrefixId === prefixIdx ? null : prefixIdx)}
                                  className="flex items-center gap-2 flex-1"
                                >
                                  <span className="px-2.5 py-1 rounded text-sm font-bold text-white" style={{ backgroundColor: '#8b5cf6' }}>
                                    {prefixGroup.prefix}
                                  </span>
                                  <span className="text-xs text-gray-400">({prefixGroup.count})</span>
                                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expandedPrefixId === prefixIdx ? 'rotate-180' : ''}`} />
                                </button>
                                <button
                                  onClick={(e) => copyProductCode(prefixGroup.prefix, e)}
                                  className="px-2 py-1 rounded bg-purple-600 hover:bg-purple-500 transition-colors flex items-center gap-1 text-[10px] text-white font-medium"
                                  title="Copy prefix code"
                                >
                                  <Copy className="w-3 h-3" />
                                  Copy
                                </button>
                              </div>
                              
                              {/* Expanded Products Dropdown */}
                              {expandedPrefixId === prefixIdx && (
                                <div className="bg-black/30 border-t border-purple-500/30">
                                  <div className="max-h-48 overflow-y-auto">
                                    {prefixGroup.products.map((product, idx) => (
                                      <div key={`${prefixGroup.prefix}-${idx}`} className="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-white/5 border-b border-white/5 last:border-0">
                                        <span className="font-mono text-purple-300 whitespace-nowrap">{product.code}</span>
                                        <span className="text-gray-400 truncate flex-1">{product.description}</span>
                                        <button
                                          onClick={(e) => copyProductCode(product.code, e)}
                                          className="p-1 rounded bg-gray-700 hover:bg-gray-600 transition-colors flex-shrink-0"
                                          title="Copy product code"
                                        >
                                          <Copy className="w-2.5 h-2.5 text-white" />
                                        </button>
                                      </div>
                                    ))}
                                    {prefixGroup.count > 10 && (
                                      <div className="text-[10px] text-gray-500 italic px-3 py-1.5 text-center">
                                        +{prefixGroup.count - 10} more products
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                    
                    {/* Goods In Results - Show FIRST when searching numbers (but not 30X) */}
                    {showGoodsInFirst && (activeTab === 'all' || activeTab === 'goodsin') && (
                      <>
                        {upcomingGoodsIn.length > 0 && (
                          <>
                            <div className="flex items-center gap-1.5 px-1 py-1.5">
                              <Package className="w-3 h-3 text-green-400" />
                              <span className="text-xs font-medium text-green-400">Goods In - Upcoming ({upcomingGoodsIn.length})</span>
                            </div>
                            {upcomingGoodsIn.map((item, idx) => (
                              <div key={`gi-up-${idx}`} className="rounded-lg overflow-hidden">
                                <button
                                  onClick={() => handleGoodsInClick(item, `gi-up-${idx}`)}
                                  className={`w-full text-left p-2.5 transition-colors border-l-2 border-green-500 ${
                                    expandedGoodsInId === `gi-up-${idx}` ? 'bg-white/20' : 'bg-white/5 hover:bg-white/10'
                                  }`}
                                >
                                  <div className="flex items-center gap-2">
                                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold text-white bg-green-600">
                                      #{item.order_number}
                                    </span>
                                    <span className="font-medium text-sm text-white">{item.supplier_name}</span>
                                    {item.status && (
                                      <span className={`ml-auto px-1.5 py-0.5 rounded text-[9px] font-bold text-white ${
                                        item.status === 'complete' ? 'bg-green-500' : item.status === 'failed' ? 'bg-red-500' : ''
                                      }`}>
                                        {item.status === 'complete' ? '✓' : item.status === 'failed' ? '✗' : ''}
                                      </span>
                                    )}
                                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expandedGoodsInId === `gi-up-${idx}` ? 'rotate-180' : ''}`} />
                                  </div>
                                  <p className="text-xs text-green-300 mt-1">{formatTransportDate(item.date)}</p>
                                </button>
                                {expandedGoodsInId === `gi-up-${idx}` && renderGoodsInDetails(item)}
                              </div>
                            ))}
                          </>
                        )}
                        
                        {pastGoodsIn.length > 0 && (
                          <>
                            <div className="flex items-center gap-1.5 px-1 py-1.5 mt-2">
                              <Package className="w-3 h-3" style={{ color: enconOrange }} />
                              <span className="text-xs font-medium" style={{ color: enconOrange }}>Goods In - Past ({pastGoodsIn.length})</span>
                            </div>
                            {pastGoodsIn.map((item, idx) => (
                              <div key={`gi-past-${idx}`} className="rounded-lg overflow-hidden">
                                <button
                                  onClick={() => handleGoodsInClick(item, `gi-past-${idx}`)}
                                  className={`w-full text-left p-2.5 transition-colors border-l-2 ${
                                    expandedGoodsInId === `gi-past-${idx}` ? 'bg-white/20' : 'bg-white/5 hover:bg-white/10'
                                  }`}
                                  style={{ borderLeftColor: enconOrange }}
                                >
                                  <div className="flex items-center gap-2">
                                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold text-white" style={{ backgroundColor: enconOrange }}>
                                      #{item.order_number}
                                    </span>
                                    <span className="font-medium text-sm text-white">{item.supplier_name}</span>
                                    {item.status && (
                                      <span className={`ml-auto px-1.5 py-0.5 rounded text-[9px] font-bold text-white ${
                                        item.status === 'complete' ? 'bg-green-500' : item.status === 'failed' ? 'bg-red-500' : ''
                                      }`}>
                                        {item.status === 'complete' ? '✓' : item.status === 'failed' ? '✗' : ''}
                                      </span>
                                    )}
                                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expandedGoodsInId === `gi-past-${idx}` ? 'rotate-180' : ''}`} />
                                  </div>
                                  <p className="text-xs mt-1" style={{ color: '#ffb366' }}>{formatTransportDate(item.date)}</p>
                                </button>
                                {expandedGoodsInId === `gi-past-${idx}` && renderGoodsInDetails(item)}
                              </div>
                            ))}
                          </>
                        )}
                      </>
                    )}
                    
                    {/* Expenses Results - Show in normal position if NOT shown first */}
                    {!showExpensesFirst && filtered.expenses.length > 0 && (activeTab === 'all' || activeTab === 'expenses') && (
                      <>
                        <div className="flex items-center gap-1.5 px-1 py-1.5 mt-2">
                          <Receipt className="w-3 h-3" style={{ color: '#3b82f6' }} />
                          <span className="text-xs font-medium" style={{ color: '#3b82f6' }}>P.O. Expenses ({filtered.expenses.length})</span>
                        </div>
                        {filtered.expenses.map((expense) => (
                          <div 
                            key={`exp-${expense.id}`}
                            className="rounded-lg overflow-hidden bg-white/5 hover:bg-white/10 border-l-2 border-blue-500 mb-1.5 cursor-pointer"
                            onClick={() => handleExpenseClick(expense)}
                          >
                            <div className="p-2.5">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold text-white bg-blue-600">
                                  {expense.po_number}
                                </span>
                                <span className="font-medium text-sm text-white flex-1 truncate">{expense.supplier || 'No Supplier'}</span>
                                <span className="text-xs text-green-400 font-semibold">£{(expense.total_cost || 0).toFixed(2)}</span>
                              </div>
                              <div className="flex items-center justify-between mt-1 flex-wrap gap-1">
                                <div className="flex items-center gap-2 text-[10px] text-gray-400">
                                  <span>{expense.order_date}</span>
                                  {expense.po_type && <span className="capitalize">• {expense.po_type.replace(/_/g, ' ')}</span>}
                                </div>
                                <div className="flex items-center gap-1">
                                  {expense.linked_transfer_id && (
                                    <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-purple-500 text-white">Linked</span>
                                  )}
                                  {expense.status === 'cancelled' && (
                                    <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-500 text-white">Cancelled</span>
                                  )}
                                  <button
                                    className="px-2 py-0.5 rounded text-[10px] font-bold text-white flex items-center gap-1"
                                    style={{ backgroundColor: enconOrange }}
                                    onClick={(e) => { e.stopPropagation(); handleExpenseClick(expense); }}
                                  >
                                    <ExternalLink className="w-3 h-3" />
                                    Open P.O.
                                  </button>
                                </div>
                              </div>
                              {expense.customer_recharge_amount > 0 && (
                                <p className="text-[10px] text-blue-300 mt-0.5">Recharge: £{expense.customer_recharge_amount.toFixed(2)}</p>
                              )}
                              {expense.m4_order_no && (
                                <p className="text-[10px] text-gray-500 mt-0.5">M4: {expense.m4_order_no}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                    
                    {/* Error Log Results - Mobile */}
                    {hasErrorLogPermission && filtered.errorLog.length > 0 && (activeTab === 'all' || activeTab === 'errorlog') && (
                      <>
                        <div className="flex items-center gap-1.5 px-1 py-1.5 mt-2">
                          <AlertTriangle className="w-3 h-3 text-red-400" />
                          <span className="text-xs font-medium text-red-400">Error Log ({filtered.errorLog.length})</span>
                        </div>
                        {filtered.errorLog.map((entry) => (
                          <div 
                            key={`errlog-${entry.id}`}
                            className="rounded-lg overflow-hidden bg-white/5 hover:bg-white/10 border-l-2 border-red-500 mb-1.5"
                          >
                            <div className="p-2.5">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold text-white bg-red-600">
                                  #{entry.order_number}
                                </span>
                                <span className="font-medium text-sm text-white flex-1 truncate">{entry.customer_name || 'Unknown'}</span>
                                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold text-white ${entry.status === 'resolved' ? 'bg-green-500' : 'bg-yellow-500'}`}>
                                  {entry.status || 'active'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 mt-1 text-[10px] text-gray-400">
                                <span>{entry.date}</span>
                                {entry.error_types && entry.error_types.length > 0 && (
                                  <span className="text-red-300">• {entry.error_types.join(', ')}</span>
                                )}
                              </div>
                              {entry.what_happened && (
                                <p className="text-[10px] text-gray-400 mt-1 truncate">{entry.what_happened}</p>
                              )}
                              <div className="flex items-center gap-2 mt-2">
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleViewErrorLog(entry); }}
                                  className="px-2 py-1 rounded text-[10px] font-semibold bg-gray-600 text-white flex items-center gap-1"
                                >
                                  <Eye className="w-3 h-3" />
                                  View
                                </button>
                                <button
                                  data-errorlog-edit="true"
                                  onClick={(e) => { e.stopPropagation(); handleErrorLogEdit(entry); }}
                                  className="px-2 py-1 rounded text-[10px] font-semibold bg-red-600 text-white flex items-center gap-1"
                                >
                                  <Edit className="w-3 h-3" />
                                  Edit
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                    
                    {renderOrderOnlyCard()}
                    {/* Upcoming Transport Deliveries - Show first */}
                    {upcomingTransport.length > 0 && (activeTab === 'all' || activeTab === 'transport') && (
                      <>
                        <div className="flex items-center gap-1.5 px-1 py-1.5">
                          <Truck className="w-3 h-3 text-green-400" />
                          <span className="text-xs font-medium text-green-400">Upcoming Deliveries ({upcomingTransport.length})</span>
                        </div>
                        {upcomingTransport.map((item, idx) => (
                          <div key={`upcoming-${idx}`} className="rounded-lg overflow-hidden bg-white/5 hover:bg-white/10 border-l-2 border-green-500 mb-1.5">
                            <div className="p-2.5">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold text-white bg-green-600">
                                  #{item.order_number}
                                </span>
                                {item.order_numbers && item.order_numbers.length > 0 && item.order_numbers.map((on, onIdx) => (
                                  <span key={onIdx} className="px-1.5 py-0.5 rounded text-[10px] font-bold text-white bg-green-500/70">
                                    #{on}
                                  </span>
                                ))}
                                <span className="font-medium text-sm text-white flex-1 truncate">{item.customer_name}</span>
                                {item.vehicle_registration && (
                                  <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-gray-600 text-white">
                                    {item.vehicle_registration}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center justify-between mt-1.5">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-green-300">{formatTransportDate(item.date)}</span>
                                  {item.run_name && item.run_name !== 'Run 1' && (
                                    <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-purple-600 text-white">
                                      {item.run_name}
                                    </span>
                                  )}
                                </div>
                                {renderOpenOrderBtn(item)}
                                {hasTransportPermission && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); navigateToTransport(item); }}
                                    className="px-2 py-1 rounded text-[10px] font-semibold text-white flex items-center gap-1"
                                    style={{ backgroundColor: enconOrange }}
                                  >
                                    <ExternalLink className="w-3 h-3" />
                                    View
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                    
                    {/* Past Transport Deliveries - Show directly for order number search, dropdown for customer name search */}
                    {pastTransport.length > 0 && (activeTab === 'all' || activeTab === 'transport') && (
                      isNumericSearch ? (
                        /* Order number search - show results directly */
                        <>
                          <div className="flex items-center gap-1.5 px-1 py-1.5 mt-2">
                            <Truck className="w-3 h-3" style={{ color: enconOrange }} />
                            <span className="text-xs font-medium" style={{ color: enconOrange }}>Order Results ({pastTransport.length})</span>
                          </div>
                          {pastTransport.map((item, idx) => (
                            <div 
                              key={`order-${idx}`} 
                              className="rounded-lg overflow-hidden bg-white/5 hover:bg-white/10 border-l-2 mb-1.5"
                              style={{ borderLeftColor: enconOrange }}
                            >
                              <div className="p-2.5">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="px-1.5 py-0.5 rounded text-[10px] font-bold text-white" style={{ backgroundColor: enconOrange }}>
                                    #{item.order_number}
                                  </span>
                                  {item.order_numbers && item.order_numbers.length > 0 && item.order_numbers.map((on, onIdx) => (
                                    <span key={onIdx} className="px-1.5 py-0.5 rounded text-[10px] font-bold text-white" style={{ backgroundColor: '#d4650a' }}>
                                      #{on}
                                    </span>
                                  ))}
                                  <span className="font-medium text-sm text-white flex-1 truncate">{item.customer_name}</span>
                                  {item.vehicle_registration && (
                                    <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-gray-600 text-white">
                                      {item.vehicle_registration}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center justify-between mt-1.5">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs" style={{ color: '#ffb366' }}>{formatTransportDate(item.date)}</span>
                                    {item.run_name && item.run_name !== 'Run 1' && (
                                      <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-purple-600 text-white">
                                        {item.run_name}
                                      </span>
                                    )}
                                  </div>
                                  {renderOpenOrderBtn(item)}
                                  {hasTransportPermission && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); navigateToTransport(item); }}
                                      className="px-2 py-1 rounded text-[10px] font-semibold text-white flex items-center gap-1"
                                      style={{ backgroundColor: '#d4650a' }}
                                    >
                                      <ExternalLink className="w-3 h-3" />
                                      View
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </>
                      ) : (
                        /* Customer name search - show in collapsible dropdown */
                        <div className="mb-2 mt-2">
                        <button
                          onClick={() => setPastDeliveriesExpanded(!pastDeliveriesExpanded)}
                          className="w-full rounded-lg p-2.5 flex items-center justify-between transition-colors"
                          style={{ 
                            backgroundColor: enconOrange,
                            border: `2px solid ${enconOrange}`
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <Truck className="w-4 h-4 text-white" />
                            <span className="text-sm font-semibold text-white">Past Deliveries ({pastTransport.length})</span>
                          </div>
                          <ChevronDown className={`w-5 h-5 text-white transition-transform ${pastDeliveriesExpanded ? 'rotate-180' : ''}`} />
                        </button>
                        {pastDeliveriesExpanded && (
                          <div className="mt-1.5 space-y-1.5 animate-slideDown">
                            {pastTransport.map((item, idx) => (
                              <div 
                                key={`past-${idx}`} 
                                className="rounded-lg overflow-hidden bg-white/5 hover:bg-white/10 border-l-2 mb-1.5"
                                style={{ borderLeftColor: enconOrange }}
                              >
                                <div className="p-2.5">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold text-white" style={{ backgroundColor: enconOrange }}>
                                      #{item.order_number}
                                    </span>
                                    {item.order_numbers && item.order_numbers.length > 0 && item.order_numbers.map((on, onIdx) => (
                                      <span key={onIdx} className="px-1.5 py-0.5 rounded text-[10px] font-bold text-white" style={{ backgroundColor: '#d4650a' }}>
                                        #{on}
                                      </span>
                                    ))}
                                    <span className="font-medium text-sm text-white flex-1 truncate">{item.customer_name}</span>
                                    {item.vehicle_registration && (
                                      <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-gray-600 text-white">
                                        {item.vehicle_registration}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center justify-between mt-1.5">
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs" style={{ color: '#ffb366' }}>{formatTransportDate(item.date)}</span>
                                      {item.run_name && item.run_name !== 'Run 1' && (
                                        <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-purple-600 text-white">
                                          {item.run_name}
                                        </span>
                                      )}
                                    </div>
                                    {renderOpenOrderBtn(item)}
                                    {hasTransportPermission && (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); navigateToTransport(item); }}
                                        className="px-2 py-1 rounded text-[10px] font-semibold text-white flex items-center gap-1"
                                        style={{ backgroundColor: '#d4650a' }}
                                      >
                                        <ExternalLink className="w-3 h-3" />
                                        View
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      )
                    )}
                    
                    {/* Customer Results - Directly after Transport */}
                    {hasCustomerPermission && filtered.customers.length > 0 && (activeTab === 'all' || activeTab === 'customers') && (
                      <>
                        <div className="flex items-center gap-1.5 px-1 py-1.5 mt-2">
                          <Users className="w-3 h-3 text-blue-400" />
                          <span className="text-xs font-medium text-blue-400">Customers ({filtered.customers.length})</span>
                        </div>
                        {filtered.customers.map((customer) => (
                          <div 
                            key={`customer-mobile-${customer.id}`}
                            className="rounded-lg overflow-hidden bg-white/5 hover:bg-white/10 border-l-2 border-blue-500 mb-1.5 cursor-pointer"
                            onClick={() => {
                              setIsOpen(false);
                              navigate(`/customer-management?customer=${customer.id}`);
                            }}
                          >
                            <div className="p-2.5">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Building2 className="w-3 h-3 text-blue-400" />
                                <span className="font-medium text-sm text-white flex-1 truncate">
                                  {customer.ledger_name || customer.account_name}
                                </span>
                              </div>
                              {customer.ledger_name && customer.account_name !== customer.ledger_name && (
                                <p className="text-[10px] text-gray-400 mt-0.5 truncate">{customer.account_name}</p>
                              )}
                              <div className="flex items-center gap-3 mt-1.5 text-[10px] text-gray-400">
                                {customer.contact_name && (
                                  <span className="flex items-center gap-1">
                                    <Users className="w-2.5 h-2.5" />
                                    {customer.contact_name}
                                  </span>
                                )}
                                {(customer.phone || customer.contact_phone) && (
                                  <span className="flex items-center gap-1">
                                    <Phone className="w-2.5 h-2.5" />
                                    {customer.phone || customer.contact_phone}
                                  </span>
                                )}
                                {customer.billing_code && (
                                  <span className="flex items-center gap-1">
                                    <MapPin className="w-2.5 h-2.5" />
                                    {customer.billing_code}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 mt-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setIsOpen(false);
                                    navigate(`/customer-management?customer=${customer.id}`);
                                  }}
                                  className="px-2 py-1 rounded text-[10px] font-semibold bg-blue-600 text-white flex items-center gap-1"
                                >
                                  <Eye className="w-3 h-3" />
                                  View Record
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setIsOpen(false); // Close search dropdown
                                    setSelectedCustomerForCall(customer);
                                    setShowCallNoteModal(true);
                                  }}
                                  className="px-2 py-1 rounded text-[10px] font-semibold text-white flex items-center gap-1"
                                  style={{ backgroundColor: enconOrange }}
                                >
                                  <PhoneCall className="w-3 h-3" />
                                  Log Call
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                    
                    {/* Projects Results - Mobile */}
                    {hasProjectPermission && filtered.projects.length > 0 && (activeTab === 'all' || activeTab === 'projects') && (
                      <>
                        <div className="flex items-center gap-1.5 px-1 py-1.5 mt-2">
                          <FolderKanban className="w-3 h-3" style={{ color: enconOrange }} />
                          <span className="text-xs font-medium" style={{ color: enconOrange }}>Projects ({filtered.projects.length})</span>
                        </div>
                        {filtered.projects.map((project) => (
                          <div 
                            key={`project-mobile-${project.id}`}
                            className="rounded-lg overflow-hidden bg-white/5 hover:bg-white/10 border-l-2 mb-1.5"
                            style={{ borderLeftColor: enconOrange }}
                          >
                            <div className="p-2.5">
                              <div className="flex items-center gap-2 flex-wrap">
                                <FolderKanban className="w-3 h-3" style={{ color: enconOrange }} />
                                <span className="font-medium text-sm text-white flex-1 truncate">
                                  {project.project_name || 'Unnamed Project'}
                                </span>
                              </div>
                              {project.project_value && (
                                <p className="text-[10px] text-green-400 font-semibold mt-0.5">
                                  {formatProjectValue(project.project_value)}
                                </p>
                              )}
                              <div className="flex items-center gap-3 mt-1.5 text-[10px] text-gray-400 flex-wrap">
                                {project.city && (
                                  <span className="flex items-center gap-1">
                                    <MapPin className="w-2.5 h-2.5" />
                                    {project.city}{project.postcode ? `, ${project.postcode}` : ''}
                                  </span>
                                )}
                                {project.market_sector && (
                                  <span className="truncate">{project.market_sector}</span>
                                )}
                              </div>
                              {/* Associated Customers Dropdown */}
                              {project.sub_contractors && project.sub_contractors.length > 0 && (
                                <div className="mt-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setExpandedProjectId(expandedProjectId === project.id ? null : project.id);
                                    }}
                                    className="flex items-center gap-1 text-[10px] text-blue-400 hover:text-blue-300"
                                  >
                                    <Users className="w-2.5 h-2.5" />
                                    {project.sub_contractors.length} Customer{project.sub_contractors.length !== 1 ? 's' : ''}
                                    <ChevronDown className={`w-3 h-3 transition-transform ${expandedProjectId === project.id ? 'rotate-180' : ''}`} />
                                  </button>
                                  {expandedProjectId === project.id && (
                                    <div className="mt-1 pl-3 space-y-0.5 animate-slideDown">
                                      {project.sub_contractors.map((sc, idx) => (
                                        <div 
                                          key={`sc-${project.id}-${idx}`}
                                          className="text-[10px] text-gray-300 truncate cursor-pointer hover:text-white"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setIsOpen(false);
                                            navigate(`/customer-management?search=${encodeURIComponent(sc.customer_name || '')}`);
                                          }}
                                        >
                                          • {sc.customer_name || 'Unknown'}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                              <div className="flex items-center gap-2 mt-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setIsOpen(false);
                                    navigate(`/projects?highlight=${project.id}`);
                                  }}
                                  className="px-2 py-1 rounded text-[10px] font-semibold text-white flex items-center gap-1"
                                  style={{ backgroundColor: enconOrange }}
                                >
                                  <Eye className="w-3 h-3" />
                                  View Project
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                    
                    {/* Stock Results */}
                    {filtered.stock.length > 0 && (activeTab === 'all' || activeTab === 'stock') && (
                      <>
                        <div className="flex items-center justify-between px-1 py-1.5 mt-2">
                          <div className="flex items-center gap-1.5">
                            <Package className="w-3 h-3 text-green-400" />
                            <span className="text-xs font-medium text-green-400">Stock ({filtered.stock.length})</span>
                            {showInStockOnly && (
                              <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-green-600 text-white">
                                IN STOCK
                              </span>
                            )}
                          </div>
                          <button
                            onClick={() => setShowInStockOnly(!showInStockOnly)}
                            className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${
                              showInStockOnly 
                                ? 'bg-green-600 text-white' 
                                : 'bg-gray-700 text-gray-300'
                            }`}
                          >
                            {showInStockOnly ? 'All' : 'In Stock'}
                          </button>
                        </div>
                        {filtered.stock.map((item, idx) => (
                          <div key={`stock-${item.product_code}`} className="rounded-lg overflow-hidden">
                            <div
                              role="button"
                              tabIndex={0}
                              onClick={() => handleStockClick(item, idx)}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleStockClick(item, idx); } }}
                              className={`w-full text-left p-2.5 transition-colors cursor-pointer ${
                                expandedStockId === idx ? 'bg-white/20' : 'bg-white/5 hover:bg-white/10'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                {isOutOfStock(item) && (
                                  <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-600 text-white">
                                    O.O.S
                                  </span>
                                )}
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold text-white" style={{ background: enconOrange }}>
                                  {item.prefix}
                                </span>
                                <span className="font-medium text-sm text-white">{item.product_code}</span>
                                <span className="text-gray-400 text-xs truncate flex-1">{item.description}</span>
                                <div className="ml-auto flex items-center gap-2">
                                  <button
                                    onClick={(e) => copyProductCode(item.product_code, e)}
                                    className="p-1 rounded bg-gray-700 hover:bg-gray-600 transition-colors"
                                    title="Copy code"
                                  >
                                    <Copy className="w-3 h-3 text-white" />
                                  </button>
                                  <span className="text-green-400 text-xs font-medium">{item.on_hand_quantity || item.on_hand_balance || item.free_stock || 0}</span>
                                  {(item.on_order_quantity || 0) > 0 && (
                                    <span className="text-yellow-400 text-xs font-medium">+{item.on_order_quantity}</span>
                                  )}
                                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expandedStockId === idx ? 'rotate-180' : ''}`} />
                                </div>
                              </div>
              {item.other_branches?.length > 0 && (
                                <div className="flex flex-nowrap items-center gap-1.5 mt-1.5 overflow-x-auto pb-1" onClick={(e) => e.stopPropagation()}>
                                  {item.other_branches.map((ob, obIdx) => {
                                    const total = (ob.on_hand ?? 0) - (ob.due_out ?? 0);
                                    return (
                                    <DropdownMenu key={ob.branch_id} modal={false}>
                                      <DropdownMenuTrigger asChild>
                                        <button
                                          type="button"
                                          title={ob.branch_name}
                                          className={`flex-shrink-0 flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 hover:bg-gray-200 hover:ring-2 hover:ring-offset-1 border-2 transition-all cursor-pointer ${
                                            obIdx % 2 === 0 ? 'border-orange-500 hover:ring-orange-400' : 'border-purple-600 hover:ring-purple-400'
                                          }`}
                                          data-testid={`other-branch-box-${item.product_code}-${ob.branch_id}`}
                                        >
                                          <span className="text-[9px] font-bold text-gray-700 underline">{ob.branch_code}</span>
                                          <span className="text-[10px] font-bold text-emerald-600">{total}</span>
                                        </button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="start" className="z-[70] bg-white rounded-xl border-2 border-orange-500 w-[130px] p-2" data-branch-transfer-menu="">
                                        <div className="flex flex-col gap-0.5 px-1 pb-2">
                                          <span className="text-[11px] leading-tight">
                                            <span className="font-bold text-gray-700 underline">{ob.branch_code}</span>
                                            <span className="text-gray-500">:</span>{' '}
                                            <span className="font-bold text-emerald-600">{total}</span>
                                          </span>
                                          <span className="text-[11px] leading-tight font-semibold text-orange-600">(OH {ob.on_hand ?? 0})</span>
                                          <span className="text-[11px] leading-tight font-semibold text-purple-600">In: {ob.incoming ?? 0}</span>
                                          <span className="text-[11px] leading-tight font-semibold text-red-500">Out: {ob.due_out ?? 0}</span>
                                        </div>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                          onClick={() => handleTransferToBranch(item, ob)}
                                          data-testid={`transfer-option-${item.product_code}-${ob.branch_id}`}
                                          className="justify-center rounded-full bg-blue-600 text-white font-semibold text-[11px] hover:bg-blue-700 focus:bg-blue-700 focus:text-white mt-1 py-1.5"
                                        >
                                          <ArrowRightLeft className="w-3.5 h-3.5 mr-1.5" />
                                          Transfer
                                        </DropdownMenuItem>
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                            {expandedStockId === idx && renderStockDetails(item)}
                          </div>
                        ))}
                      </>
                    )}
                    
                    {/* Goods In Results - Show at end if NOT numeric search */}
                    {!showGoodsInFirst && (activeTab === 'all' || activeTab === 'goodsin') && (
                      <>
                        {upcomingGoodsIn.length > 0 && (
                          <>
                            <div className="flex items-center gap-1.5 px-1 py-1.5 mt-2">
                              <Package className="w-3 h-3 text-green-400" />
                              <span className="text-xs font-medium text-green-400">Goods In - Upcoming ({upcomingGoodsIn.length})</span>
                            </div>
                            {upcomingGoodsIn.map((item, idx) => (
                              <div key={`gi-up-${idx}`} className="rounded-lg overflow-hidden">
                                <button
                                  onClick={() => handleGoodsInClick(item, `gi-up-${idx}`)}
                                  className={`w-full text-left p-2.5 transition-colors border-l-2 border-green-500 ${
                                    expandedGoodsInId === `gi-up-${idx}` ? 'bg-white/20' : 'bg-white/5 hover:bg-white/10'
                                  }`}
                                >
                                  <div className="flex items-center gap-2">
                                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold text-white bg-green-600">
                                      #{item.order_number}
                                    </span>
                                    <span className="font-medium text-sm text-white">{item.supplier_name}</span>
                                    {item.status && (
                                      <span className={`ml-auto px-1.5 py-0.5 rounded text-[9px] font-bold text-white ${
                                        item.status === 'complete' ? 'bg-green-500' : item.status === 'failed' ? 'bg-red-500' : ''
                                      }`}>
                                        {item.status === 'complete' ? '✓' : item.status === 'failed' ? '✗' : ''}
                                      </span>
                                    )}
                                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expandedGoodsInId === `gi-up-${idx}` ? 'rotate-180' : ''}`} />
                                  </div>
                                  <p className="text-xs text-green-300 mt-1">{formatTransportDate(item.date)}</p>
                                </button>
                                {expandedGoodsInId === `gi-up-${idx}` && renderGoodsInDetails(item)}
                              </div>
                            ))}
                          </>
                        )}
                        
                        {pastGoodsIn.length > 0 && (
                          <>
                            <div className="flex items-center gap-1.5 px-1 py-1.5 mt-2">
                              <Package className="w-3 h-3" style={{ color: enconOrange }} />
                              <span className="text-xs font-medium" style={{ color: enconOrange }}>Goods In - Past ({pastGoodsIn.length})</span>
                            </div>
                            {pastGoodsIn.map((item, idx) => (
                              <div key={`gi-past-${idx}`} className="rounded-lg overflow-hidden">
                                <button
                                  onClick={() => handleGoodsInClick(item, `gi-past-${idx}`)}
                                  className={`w-full text-left p-2.5 transition-colors border-l-2 ${
                                    expandedGoodsInId === `gi-past-${idx}` ? 'bg-white/20' : 'bg-white/5 hover:bg-white/10'
                                  }`}
                                  style={{ borderLeftColor: enconOrange }}
                                >
                                  <div className="flex items-center gap-2">
                                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold text-white" style={{ backgroundColor: enconOrange }}>
                                      #{item.order_number}
                                    </span>
                                    <span className="font-medium text-sm text-white">{item.supplier_name}</span>
                                    {item.status && (
                                      <span className={`ml-auto px-1.5 py-0.5 rounded text-[9px] font-bold text-white ${
                                        item.status === 'complete' ? 'bg-green-500' : item.status === 'failed' ? 'bg-red-500' : ''
                                      }`}>
                                        {item.status === 'complete' ? '✓' : item.status === 'failed' ? '✗' : ''}
                                      </span>
                                    )}
                                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expandedGoodsInId === `gi-past-${idx}` ? 'rotate-180' : ''}`} />
                                  </div>
                                  <p className="text-xs mt-1" style={{ color: '#ffb366' }}>{formatTransportDate(item.date)}</p>
                                </button>
                                {expandedGoodsInId === `gi-past-${idx}` && renderGoodsInDetails(item)}
                              </div>
                            ))}
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Stock Modal */}
        {showStockModal && selectedStock && createPortal(
          <StockModal stock={selectedStock} onClose={() => setShowStockModal(false)} />,
          document.body
        )}
        
        {/* Goods In Edit Modal */}
        {showGoodsInEditModal && editingGoodsIn && (
          <GoodsInEditModal
            entry={editingGoodsIn}
            form={goodsInForm}
            setForm={setGoodsInForm}
            onSave={handleUpdateGoodsIn}
            onClose={() => { setShowGoodsInEditModal(false); setEditingGoodsIn(null); }}
          />
        )}

        {/* Call Note Modal - Mobile */}
        <CallNoteModal
          isOpen={showCallNoteModal}
          onClose={() => { setShowCallNoteModal(false); setSelectedCustomerForCall(null); }}
          customer={selectedCustomerForCall}
          showViewOption={true}
          onViewInCallsLog={(customer) => {
            setShowCallNoteModal(false);
            setIsOpen(false);
            navigate(`/customer-management?customer=${customer.id}&tab=calls`);
          }}
        />
      </>
    );
  }

  // Desktop/Tablet: embossed grey/dark-gradient bar, always visible, dead-centre
  // of the sub-header — replaces the old click-to-open icon+dropdown. Typing
  // directly opens the results panel, which slides down right below it.
  return (
    <>
      <div className="hidden md:flex items-center gap-2" data-search-trigger>
        <button
          type="button"
          onClick={() => setMode('customer')}
          data-testid="global-search-customer-toggle"
          title="Search customers"
          className="h-9 px-3.5 rounded-xl text-[11px] font-bold uppercase tracking-wide whitespace-nowrap border-2 transition-all duration-200 shrink-0"
          style={{
            backgroundColor: searchMode === 'customer' ? enconOrange : 'white',
            color: searchMode === 'customer' ? 'white' : enconOrange,
            borderColor: enconOrange,
            boxShadow: searchMode === 'customer' ? '0 2px 8px rgba(238,114,3,0.4)' : 'none',
          }}
        >
          Customer
        </button>

      <div
        data-search-trigger
        data-search-mode={searchMode}
        className={`relative flex items-center w-[300px] lg:w-[360px] h-9 rounded-xl px-3.5 gap-2 transition-all duration-200 ${
          isOpen ? 'ring-2 ring-orange-400/70' : ''
        }`}
        style={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.22) 100%)',
          backdropFilter: 'blur(18px) saturate(180%)',
          WebkitBackdropFilter: 'blur(18px) saturate(180%)',
          boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.7), inset 0 -1px 2px rgba(0,0,0,0.06), 0 4px 14px rgba(0,0,0,0.12), 0 0 0 1px rgba(238,114,3,0.55)',
          border: '1px solid rgba(255,255,255,0.5)',
        }}
      >
        <Search
          className={`w-4 h-4 shrink-0 ${isDarkBgPage ? 'text-white' : 'text-black'}`}
          style={{ filter: isDarkBgPage ? 'drop-shadow(0 1px 1px rgba(0,0,0,0.85))' : 'drop-shadow(0 1px 1px rgba(255,255,255,0.85))' }}
        />
        <input
          ref={searchInputRef}
          type="text"
          value={searchQuery}
          onChange={(e) => {
            const val = e.target.value;
            setSearchQuery(val);
            resetExpanded();
            setIsOpen(!!val);
          }}
          onFocus={() => { if (searchQuery) setIsOpen(true); }}
          onKeyDown={handleKeyDown}
          placeholder={
            searchMode === 'customer' ? 'Search customers…'
              : searchMode === 'project' ? 'Search projects, address, postcode, Barbour ID…'
              : 'Search stock, transport, goods in...'
          }
          className={`flex-1 min-w-0 bg-transparent border-0 outline-none text-sm ${isDarkBgPage ? 'text-white placeholder-white/70' : 'text-black placeholder-black/70'}`}
          style={{ textShadow: isDarkBgPage ? '0 1px 1px rgba(0,0,0,0.85)' : '0 1px 1px rgba(255,255,255,0.85)' }}
          data-testid="global-search-subheader-input"
          autoComplete="off"
        />
        {searchQuery && (
          <button
            onClick={() => { setSearchQuery(''); setIsOpen(false); resetExpanded(); }}
            className={`transition-colors shrink-0 ${isDarkBgPage ? 'text-white hover:text-gray-200' : 'text-black hover:text-gray-700'}`}
            style={{ filter: isDarkBgPage ? 'drop-shadow(0 1px 1px rgba(0,0,0,0.85))' : 'drop-shadow(0 1px 1px rgba(255,255,255,0.85))' }}
            data-testid="global-search-clear-btn"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

        <button
          type="button"
          onClick={() => setMode('project')}
          data-testid="global-search-project-toggle"
          title="Search projects"
          className="h-9 px-3.5 rounded-xl text-[11px] font-bold uppercase tracking-wide whitespace-nowrap border-2 transition-all duration-200 shrink-0"
          style={{
            backgroundColor: searchMode === 'project' ? tealTeal : 'white',
            color: searchMode === 'project' ? 'white' : tealTeal,
            borderColor: tealTeal,
            boxShadow: searchMode === 'project' ? '0 2px 8px rgba(13,148,136,0.4)' : 'none',
          }}
        >
          Projects
        </button>
      </div>

      {isOpen && searchQuery && searchMode === 'global' && createPortal(
        <div
          ref={dropdownRef}
          className="fixed left-0 right-0 z-[60] animate-slideDown"
          style={{ top: `${dropdownTop}px` }}
        >
          <div
            className="w-full"
            style={{
              background: 'rgba(45, 45, 45, 0.97)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              borderBottom: '2px solid rgba(238, 114, 3, 0.5)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4)'
            }}
          >
            <div className="px-4 lg:px-8 py-5">
              {/* Tabs */}
              <div className="flex gap-2 mb-4 overflow-x-auto">
                {['all', 'prefix', 'stock', 'transport', 'goodsin', 'expenses', ...(hasErrorLogPermission ? ['errorlog'] : []), ...(hasCustomerPermission ? ['customers'] : []), ...(hasProjectPermission ? ['projects'] : [])].map((tab) => {
                  // Get result count for each tab
                  const getTabCount = () => {
                    switch(tab) {
                      case 'all': return totalResults;
                      case 'prefix': return prefixResults.length;
                      case 'stock': return stockResults.length;
                      case 'transport': return transportResults.length;
                      case 'goodsin': return goodsInResults.length;
                      case 'expenses': return expensesResults.length;
                      case 'errorlog': return errorLogResults.length;
                      case 'customers': return customerResults.length;
                      case 'projects': return projectResults.length;
                      default: return 0;
                    }
                  };
                  // Get tab color based on type
                  const getTabColor = () => {
                    switch(tab) {
                      case 'all': return enconOrange;
                      case 'prefix': return '#8b5cf6'; // purple
                      case 'stock': return '#22c55e'; // green
                      case 'transport': return '#22c55e'; // green
                      case 'goodsin': return '#22c55e'; // green
                      case 'expenses': return '#3b82f6'; // blue
                      case 'errorlog': return '#ef4444'; // red
                      case 'customers': return '#3b82f6'; // blue
                      case 'projects': return enconOrange;
                      default: return enconOrange;
                    }
                  };
                  const count = getTabCount();
                  const hasResults = count > 0;
                  const tabColor = getTabColor();
                  const isActive = activeTab === tab;
                  
                  return (
                    <button
                      key={tab}
                      onClick={() => { setActiveTab(tab); resetExpanded(); }}
                      className={`px-4 py-2 rounded-lg font-medium text-sm transition-all whitespace-nowrap flex items-center gap-2 ${
                        isActive
                          ? 'text-white shadow-lg'
                          : hasResults 
                            ? 'text-white hover:opacity-80'
                            : 'text-gray-400 hover:text-white hover:bg-white/5'
                      }`}
                      style={{
                        background: isActive ? tabColor : hasResults ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.08)',
                        border: isActive ? 'none' : hasResults ? `2px solid ${tabColor}` : '1px solid rgba(255,255,255,0.1)'
                      }}
                    >
                      <span>{tab === 'all' ? 'All' : 
                             tab === 'prefix' ? 'Prefix' :
                             tab === 'stock' ? 'Stock' : 
                             tab === 'transport' ? 'Transport' :
                             tab === 'expenses' ? 'Expenses' :
                             tab === 'errorlog' ? 'Error Log' :
                             tab === 'customers' ? 'Customers' :
                             tab === 'projects' ? 'Projects' :
                             'Goods In'}</span>
                      {hasResults && (
                        <span 
                          className="px-1.5 py-0.5 rounded-full text-xs font-bold"
                          style={{ 
                            backgroundColor: isActive ? 'rgba(255,255,255,0.3)' : tabColor,
                            color: 'white'
                          }}
                        >
                          {count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              
              {/* Results Area */}
              <div 
                className="max-h-[400px] overflow-y-auto rounded-xl"
                style={{ 
                  background: 'rgba(0,0,0,0.25)',
                  border: '1px solid rgba(255,255,255,0.05)'
                }}
              >
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                    <span className="ml-3 text-gray-400">Searching...</span>
                  </div>
                ) : searchQuery && !hasResults ? (
                  <div className="text-center py-12 text-gray-400">
                    <Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="text-lg">No results found for &quot;{searchQuery}&quot;</p>
                  </div>
                ) : !searchQuery ? (
                  <div className="text-center py-12 text-gray-400">
                    <Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="text-lg">Start typing to search</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-700">
                    {/* Prefix Results - ONLY show when prefix tab is explicitly selected */}
                    {prefixResults.length > 0 && activeTab === 'prefix' && (
                      <div className="p-3">
                        <div className="flex items-center gap-2 mb-3 px-2">
                          <Hash className="w-4 h-4" style={{ color: '#8b5cf6' }} />
                          <span className="text-sm font-medium" style={{ color: '#8b5cf6' }}>Prefix Codes ({prefixResults.length})</span>
                        </div>
                        <div className="space-y-1">
                          {prefixResults.map((prefixGroup, prefixIdx) => (
                            <div 
                              key={`prefix-desktop-${prefixGroup.prefix}`}
                              className="rounded-lg overflow-hidden"
                            >
                              {/* Prefix Row */}
                              <div 
                                className={`flex items-center justify-between p-3 transition-all ${expandedPrefixId === `d-${prefixIdx}` ? 'bg-purple-900/30' : 'bg-white/5 hover:bg-white/10'}`}
                              >
                                <button
                                  onClick={() => setExpandedPrefixId(expandedPrefixId === `d-${prefixIdx}` ? null : `d-${prefixIdx}`)}
                                  className="flex items-center gap-3 flex-1"
                                >
                                  <span className="px-4 py-2 rounded-lg text-lg font-bold text-white" style={{ backgroundColor: '#8b5cf6' }}>
                                    {prefixGroup.prefix}
                                  </span>
                                  <span className="text-sm text-gray-400">({prefixGroup.count} products)</span>
                                  <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${expandedPrefixId === `d-${prefixIdx}` ? 'rotate-180' : ''}`} />
                                </button>
                                <button
                                  onClick={(e) => copyProductCode(prefixGroup.prefix, e)}
                                  className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 transition-colors flex items-center gap-2 text-sm text-white font-semibold"
                                  title="Copy prefix code"
                                >
                                  <Copy className="w-4 h-4" />
                                  Copy Prefix
                                </button>
                              </div>
                              
                              {/* Expanded Products Dropdown */}
                              {expandedPrefixId === `d-${prefixIdx}` && (
                                <div className="bg-black/30 border-t border-purple-500/30">
                                  <div className="max-h-64 overflow-y-auto">
                                    {prefixGroup.products.map((product, idx) => (
                                      <div key={`${prefixGroup.prefix}-d-${idx}`} className="flex items-center gap-3 px-4 py-2 hover:bg-white/5 border-b border-white/5 last:border-0">
                                        <span className="font-mono text-purple-300 whitespace-nowrap">{product.code}</span>
                                        <span className="text-gray-400 flex-1">{product.description}</span>
                                        <button
                                          onClick={(e) => copyProductCode(product.code, e)}
                                          className="px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 transition-colors flex items-center gap-1.5 text-xs text-white flex-shrink-0"
                                          title="Copy product code"
                                        >
                                          <Copy className="w-3 h-3" />
                                          Copy
                                        </button>
                                      </div>
                                    ))}
                                    {prefixGroup.count > 10 && (
                                      <div className="text-xs text-gray-500 italic px-4 py-2 text-center">
                                        +{prefixGroup.count - 10} more products
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* P.O. Expenses Results - Show FIRST when searching 30X */}
                    {showExpensesFirst && filtered.expenses.length > 0 && (activeTab === 'all' || activeTab === 'expenses') && (
                      <div className="p-3">
                        <div className="flex items-center gap-2 mb-3 px-2">
                          <Receipt className="w-4 h-4 text-blue-400" />
                          <span className="text-sm font-medium text-blue-400">P.O. Expenses ({filtered.expenses.length})</span>
                        </div>
                        <div className="grid gap-2">
                          {filtered.expenses.map((expense) => (
                            <div 
                              key={`exp-first-${expense.id}`}
                              className="rounded-lg overflow-hidden bg-white/5 hover:bg-white/10 border-l-2 border-blue-500 p-3 cursor-pointer transition-all"
                              onClick={() => handleExpenseClick(expense)}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-3 flex-wrap">
                                    <span className="px-2 py-0.5 rounded text-xs font-bold bg-blue-600 text-white">
                                      {expense.po_number}
                                    </span>
                                    <span className="font-semibold text-white">{expense.supplier || 'No Supplier'}</span>
                                    {expense.status === 'cancelled' && (
                                      <span className="px-2 py-0.5 rounded text-xs font-bold bg-red-500 text-white">
                                        Cancelled
                                      </span>
                                    )}
                                    {expense.linked_transfer_id && (
                                      <span className="px-2 py-0.5 rounded text-xs font-semibold bg-purple-600 text-white">
                                        Linked Transfer
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                                    <span>{expense.order_date}</span>
                                    {expense.po_type && <span className="capitalize">{expense.po_type.replace(/_/g, ' ')}</span>}
                                    {expense.m4_order_no && <span>M4: {expense.m4_order_no}</span>}
                                    {expense.ordered_by && <span>By: {expense.ordered_by}</span>}
                                  </div>
                                </div>
                                <div className="flex flex-col items-end gap-1 ml-4">
                                  <span className="text-lg font-bold text-green-400">£{(expense.total_cost || 0).toFixed(2)}</span>
                                  {expense.customer_recharge_amount > 0 && (
                                    <span className="text-xs text-blue-300">Recharge: £{expense.customer_recharge_amount.toFixed(2)}</span>
                                  )}
                                  <button
                                    className="px-3 py-1 rounded text-xs font-bold text-white flex items-center gap-1 mt-1"
                                    style={{ backgroundColor: enconOrange }}
                                    onClick={(e) => { e.stopPropagation(); handleExpenseClick(expense); }}
                                  >
                                    <ExternalLink className="w-3 h-3" />
                                    Open P.O.
                                  </button>
                                </div>
                              </div>
                              {expense.notes && (
                                <p className="text-xs text-gray-500 mt-2 truncate">{expense.notes}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Goods In Results - Show FIRST when searching numbers (but not 30X) */}
                    {showGoodsInFirst && (upcomingGoodsIn.length > 0 || pastGoodsIn.length > 0) && (activeTab === 'all' || activeTab === 'goodsin') && (
                      <div className="p-3">
                        {/* Upcoming */}
                        {upcomingGoodsIn.length > 0 && (
                          <>
                            <div className="flex items-center gap-2 mb-3 px-2">
                              <Package className="w-4 h-4 text-green-400" />
                              <span className="text-sm font-medium text-green-400">Goods In - Upcoming ({upcomingGoodsIn.length})</span>
                            </div>
                            <div className="grid gap-2 mb-4">
                              {upcomingGoodsIn.map((item, idx) => (
                                <div key={`gi-up-${idx}`} className="rounded-lg overflow-hidden">
                                  <button
                                    onClick={() => handleGoodsInClick(item, `gi-up-${idx}`)}
                                    className={`w-full text-left p-3 transition-all border-l-2 border-green-500 ${
                                      expandedGoodsInId === `gi-up-${idx}` ? 'bg-white/15' : 'hover:bg-white/10'
                                    }`}
                                  >
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-3">
                                          <span className="px-2 py-0.5 rounded text-xs font-bold bg-green-600 text-white">
                                            #{item.order_number}
                                          </span>
                                          <span className="font-semibold text-white">{item.supplier_name}</span>
                                          {item.status && (
                                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold text-white ${
                                              item.status === 'complete' ? 'bg-green-500' : item.status === 'failed' ? 'bg-red-500' : ''
                                            }`}>
                                              {item.status === 'complete' ? '✓ Complete' : item.status === 'failed' ? '✗ Failed' : ''}
                                            </span>
                                          )}
                                          <ChevronDown className={`w-4 h-4 text-gray-400 ml-auto transition-transform ${expandedGoodsInId === `gi-up-${idx}` ? 'rotate-180' : ''}`} />
                                        </div>
                                      </div>
                                      <span className="text-green-300 text-sm font-medium ml-4">{formatTransportDate(item.date)}</span>
                                    </div>
                                  </button>
                                  {expandedGoodsInId === `gi-up-${idx}` && renderGoodsInDetails(item)}
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                        
                        {/* Past */}
                        {pastGoodsIn.length > 0 && (
                          <>
                            <div className="flex items-center gap-2 mb-3 px-2">
                              <Package className="w-4 h-4" style={{ color: enconOrange }} />
                              <span className="text-sm font-medium" style={{ color: enconOrange }}>Goods In - Past ({pastGoodsIn.length})</span>
                            </div>
                            <div className="grid gap-2">
                              {pastGoodsIn.map((item, idx) => (
                                <div key={`gi-past-${idx}`} className="rounded-lg overflow-hidden">
                                  <button
                                    onClick={() => handleGoodsInClick(item, `gi-past-${idx}`)}
                                    className={`w-full text-left p-3 transition-all border-l-2 ${
                                      expandedGoodsInId === `gi-past-${idx}` ? 'bg-white/15' : 'hover:bg-white/10'
                                    }`}
                                    style={{ borderLeftColor: enconOrange }}
                                  >
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-3">
                                          <span className="px-2 py-0.5 rounded text-xs font-bold text-white" style={{ backgroundColor: enconOrange }}>
                                            #{item.order_number}
                                          </span>
                                          <span className="font-semibold text-white">{item.supplier_name}</span>
                                          {item.status && (
                                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold text-white ${
                                              item.status === 'complete' ? 'bg-green-500' : item.status === 'failed' ? 'bg-red-500' : ''
                                            }`}>
                                              {item.status === 'complete' ? '✓ Complete' : item.status === 'failed' ? '✗ Failed' : ''}
                                            </span>
                                          )}
                                          <ChevronDown className={`w-4 h-4 text-gray-400 ml-auto transition-transform ${expandedGoodsInId === `gi-past-${idx}` ? 'rotate-180' : ''}`} />
                                        </div>
                                      </div>
                                      <span className="text-sm font-medium ml-4" style={{ color: '#ffb366' }}>{formatTransportDate(item.date)}</span>
                                    </div>
                                  </button>
                                  {expandedGoodsInId === `gi-past-${idx}` && renderGoodsInDetails(item)}
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                    
                    {/* Stock Results with Expandable Items - Only show on 'all' or 'stock' tabs */}
                    {filtered.stock.length > 0 && (activeTab === 'all' || activeTab === 'stock') && (
                      <div className="p-3">
                        <div className="flex items-center justify-between mb-3 px-2">
                          <div className="flex items-center gap-2">
                            <Package className="w-4 h-4 text-green-400" />
                            <span className="text-xs font-semibold text-green-400 uppercase tracking-wide">Stock Results</span>
                            {showInStockOnly && (
                              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-green-600 text-white">
                                IN STOCK ONLY
                              </span>
                            )}
                            {matchedSupplierGroup && (
                              <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-600 text-white ml-2">
                                Supplier: {matchedSupplierGroup}
                              </span>
                            )}
                          </div>
                          <button
                            onClick={() => setShowInStockOnly(!showInStockOnly)}
                            className={`px-2 py-1 rounded text-[10px] font-medium transition-colors ${
                              showInStockOnly 
                                ? 'bg-green-600 text-white' 
                                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            }`}
                            title="Press CTRL to toggle"
                          >
                            {showInStockOnly ? 'Show All' : 'In Stock Only'}
                          </button>
                        </div>
                        <div className="grid gap-2">
                          {filtered.stock.map((item, idx) => (
                            <div key={`stock-${idx}`} className="rounded-lg overflow-hidden">
                              <div
                                role="button"
                                tabIndex={0}
                                onClick={() => handleStockClick(item, idx)}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleStockClick(item, idx); } }}
                                className={`w-full text-left p-3 transition-all cursor-pointer ${
                                  expandedStockId === idx ? 'bg-white/15' : 'hover:bg-white/10'
                                }`}
                              >
                                <div className="flex items-center gap-3">
                                    {isOutOfStock(item) && (
                                      <span className="px-2 py-0.5 rounded text-xs font-bold bg-red-600 text-white">
                                        O.O.S
                                      </span>
                                    )}
                                    <span className="px-2 py-0.5 rounded text-xs font-bold" style={{ background: enconOrange, color: 'white' }}>
                                      {item.prefix || item.product_code?.substring(0, 3)}
                                    </span>
                                    <span className="font-semibold text-white">{item.product_code}</span>
                                    <span className="text-gray-300 text-sm truncate flex-1 min-w-0">{item.description}</span>
                                    <div className="flex items-center gap-4 text-white flex-shrink-0">
                                      {item.weight > 0 && (
                                        <div className="text-right">
                                          <div className="text-[9px] text-gray-400 uppercase tracking-wide">Weight</div>
                                          <span className="font-medium">{item.weight.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                          <span className="text-xs ml-1">kg</span>
                                        </div>
                                      )}
                                      <div className="text-right">
                                        <div className="text-[9px] text-gray-400 uppercase tracking-wide">On Hand</div>
                                        <span className="font-medium text-green-400">{item.on_hand_quantity || item.on_hand_balance || item.free_stock || 0}</span>
                                        <span className="text-xs ml-1">{item.stock_unit || 'units'}</span>
                                      </div>
                                      {(item.on_order_quantity || 0) > 0 && (
                                        <div className="text-right">
                                          <div className="text-[9px] text-gray-400 uppercase tracking-wide">Due In</div>
                                          <span className="font-medium">+{item.on_order_quantity}</span>
                                          <span className="text-xs ml-1">due</span>
                                        </div>
                                      )}
                                    </div>
                                    <button
                                      onClick={(e) => copyProductCode(item.product_code, e)}
                                      className="p-1.5 rounded bg-gray-700 hover:bg-gray-600 transition-colors ml-auto"
                                      title="Copy product code"
                                    >
                                      <Copy className="w-3.5 h-3.5 text-white" />
                                    </button>
                                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expandedStockId === idx ? 'rotate-180' : ''}`} />
                                  </div>
                                {item.other_branches?.length > 0 && (
                                  <div className="flex flex-nowrap items-center gap-2 mt-2 overflow-x-auto pb-1" onClick={(e) => e.stopPropagation()}>
                                    {item.other_branches.map((ob, obIdx) => {
                                      const total = (ob.on_hand ?? 0) - (ob.due_out ?? 0);
                                      return (
                                      <DropdownMenu key={ob.branch_id} modal={false}>
                                        <DropdownMenuTrigger asChild>
                                          <button
                                            type="button"
                                            title={ob.branch_name}
                                            className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100 hover:bg-gray-200 hover:ring-2 hover:ring-offset-1 border-2 transition-all cursor-pointer ${
                                              obIdx % 2 === 0 ? 'border-orange-500 hover:ring-orange-400' : 'border-purple-600 hover:ring-purple-400'
                                            }`}
                                            data-testid={`other-branch-box-large-${item.product_code}-${ob.branch_id}`}
                                          >
                                            <span className="text-[10px] font-bold text-gray-700 underline">{ob.branch_code}</span>
                                            <span className="text-[11px] font-bold text-emerald-600">{total}</span>
                                          </button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="start" className="z-[70] bg-white rounded-xl border-2 border-orange-500 w-[140px] p-2" data-branch-transfer-menu="">
                                          <div className="flex flex-col gap-0.5 px-1 pb-2">
                                            <span className="text-[12px] leading-tight">
                                              <span className="font-bold text-gray-700 underline">{ob.branch_code}</span>
                                              <span className="text-gray-500">:</span>{' '}
                                              <span className="font-bold text-emerald-600">{total}</span>
                                            </span>
                                            <span className="text-[12px] leading-tight font-semibold text-orange-600">(OH {ob.on_hand ?? 0})</span>
                                            <span className="text-[12px] leading-tight font-semibold text-purple-600">In: {ob.incoming ?? 0}</span>
                                            <span className="text-[12px] leading-tight font-semibold text-red-500">Out: {ob.due_out ?? 0}</span>
                                          </div>
                                          <DropdownMenuSeparator />
                                          <DropdownMenuItem
                                            onClick={() => handleTransferToBranch(item, ob)}
                                            data-testid={`transfer-option-large-${item.product_code}-${ob.branch_id}`}
                                            className="justify-center rounded-full bg-blue-600 text-white font-semibold text-xs hover:bg-blue-700 focus:bg-blue-700 focus:text-white mt-1 py-1.5"
                                          >
                                            <ArrowRightLeft className="w-3.5 h-3.5 mr-1.5" />
                                            Transfer
                                          </DropdownMenuItem>
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                              {expandedStockId === idx && renderStockDetails(item)}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {renderOrderOnlyCard()}
                    {/* Upcoming Transport Deliveries - Show first */}
                    {upcomingTransport.length > 0 && (activeTab === 'all' || activeTab === 'transport') && (
                      <div className="p-3">
                        <div className="flex items-center gap-2 mb-3 px-2">
                          <Truck className="w-4 h-4 text-green-400" />
                          <span className="text-sm font-medium text-green-400">Upcoming Deliveries ({upcomingTransport.length})</span>
                        </div>
                        <div className="grid gap-2">
                          {upcomingTransport.map((item, idx) => (
                            <div 
                              key={`upcoming-${idx}`} 
                              className="rounded-lg overflow-hidden bg-white/5 hover:bg-white/10 border-l-2 border-green-500 p-3"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3 flex-wrap flex-1">
                                  <span className="px-2 py-0.5 rounded text-xs font-bold bg-green-600 text-white">
                                    #{item.order_number || item.id}
                                  </span>
                                  {item.order_numbers && item.order_numbers.length > 0 && item.order_numbers.map((on, onIdx) => (
                                    <span key={onIdx} className="px-2 py-0.5 rounded text-xs font-bold bg-green-500/70 text-white">
                                      #{on}
                                    </span>
                                  ))}
                                  <span className="font-semibold text-white">{item.customer_name || 'Unknown'}</span>
                                  {item.vehicle_registration && (
                                    <span className="px-2 py-0.5 rounded text-xs font-semibold bg-gray-600 text-white">
                                      {item.vehicle_registration}
                                    </span>
                                  )}
                                  {item.run_name && item.run_name !== 'Run 1' && (
                                    <span className="px-2 py-0.5 rounded text-xs font-semibold bg-purple-600 text-white">
                                      {item.run_name}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-3 ml-4">
                                  <span className="text-green-300 text-sm font-medium">{formatTransportDate(item.date)}</span>
                                  {renderOpenOrderBtn(item)}
                                  {hasTransportPermission && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); navigateToTransport(item); }}
                                      className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white flex items-center gap-1.5 transition-colors hover:opacity-90"
                                      style={{ backgroundColor: enconOrange }}
                                    >
                                      <ExternalLink className="w-3.5 h-3.5" />
                                      View on Transport
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Past Transport Deliveries - Show directly for order number search, dropdown for customer name search */}
                    {pastTransport.length > 0 && (activeTab === 'all' || activeTab === 'transport') && (
                      isNumericSearch ? (
                        /* Order number search - show results directly */
                        <div className="p-3">
                          <div className="flex items-center gap-2 mb-3 px-2">
                            <Truck className="w-4 h-4" style={{ color: enconOrange }} />
                            <span className="text-sm font-medium" style={{ color: enconOrange }}>Order Results ({pastTransport.length})</span>
                          </div>
                          <div className="grid gap-2">
                            {pastTransport.map((item, idx) => (
                              <div 
                                key={`order-${idx}`} 
                                className="rounded-lg overflow-hidden bg-white/5 hover:bg-white/10 border-l-2 p-3"
                                style={{ borderLeftColor: enconOrange }}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3 flex-wrap flex-1">
                                    <span className="px-2 py-0.5 rounded text-xs font-bold text-white" style={{ backgroundColor: enconOrange }}>
                                      #{item.order_number || item.id}
                                    </span>
                                    {item.order_numbers && item.order_numbers.length > 0 && item.order_numbers.map((on, onIdx) => (
                                      <span key={onIdx} className="px-2 py-0.5 rounded text-xs font-bold text-white" style={{ backgroundColor: '#d4650a' }}>
                                        #{on}
                                      </span>
                                    ))}
                                    <span className="font-semibold text-white">{item.customer_name || 'Unknown'}</span>
                                    {item.vehicle_registration && (
                                      <span className="px-2 py-0.5 rounded text-xs font-semibold bg-gray-600 text-white">
                                        {item.vehicle_registration}
                                      </span>
                                    )}
                                    {item.run_name && item.run_name !== 'Run 1' && (
                                      <span className="px-2 py-0.5 rounded text-xs font-semibold bg-purple-600 text-white">
                                        {item.run_name}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-3 ml-4">
                                    <span className="text-sm font-medium" style={{ color: '#ffb366' }}>{formatTransportDate(item.date)}</span>
                                    {renderOpenOrderBtn(item)}
                                    {hasTransportPermission && (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); navigateToTransport(item); }}
                                        className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white flex items-center gap-1.5 transition-colors hover:opacity-90"
                                        style={{ backgroundColor: '#d4650a' }}
                                      >
                                        <ExternalLink className="w-3.5 h-3.5" />
                                        View on Transport
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        /* Customer name search - show in collapsible dropdown */
                        <div className="p-3">
                        <button
                          onClick={() => setPastDeliveriesExpanded(!pastDeliveriesExpanded)}
                          className="w-full rounded-lg p-3 flex items-center justify-between transition-colors mb-3"
                          style={{ 
                            backgroundColor: enconOrange,
                            border: `2px solid ${enconOrange}`
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <Truck className="w-5 h-5 text-white" />
                            <span className="text-sm font-bold text-white">Past Deliveries ({pastTransport.length})</span>
                          </div>
                          <ChevronDown className={`w-5 h-5 text-white transition-transform ${pastDeliveriesExpanded ? 'rotate-180' : ''}`} />
                        </button>
                        {pastDeliveriesExpanded && (
                          <div className="grid gap-2 animate-slideDown">
                            {pastTransport.map((item, idx) => (
                              <div 
                                key={`past-${idx}`} 
                                className="rounded-lg overflow-hidden bg-white/5 hover:bg-white/10 border-l-2 p-3"
                                style={{ borderLeftColor: enconOrange }}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3 flex-wrap flex-1">
                                    <span className="px-2 py-0.5 rounded text-xs font-bold text-white" style={{ backgroundColor: enconOrange }}>
                                      #{item.order_number || item.id}
                                    </span>
                                    {item.order_numbers && item.order_numbers.length > 0 && item.order_numbers.map((on, onIdx) => (
                                      <span key={onIdx} className="px-2 py-0.5 rounded text-xs font-bold text-white" style={{ backgroundColor: '#d4650a' }}>
                                        #{on}
                                      </span>
                                    ))}
                                    <span className="font-semibold text-white">{item.customer_name || 'Unknown'}</span>
                                    {item.vehicle_registration && (
                                      <span className="px-2 py-0.5 rounded text-xs font-semibold bg-gray-600 text-white">
                                        {item.vehicle_registration}
                                      </span>
                                    )}
                                    {item.run_name && item.run_name !== 'Run 1' && (
                                      <span className="px-2 py-0.5 rounded text-xs font-semibold bg-purple-600 text-white">
                                        {item.run_name}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-3 ml-4">
                                    <span className="text-sm font-medium" style={{ color: '#ffb366' }}>{formatTransportDate(item.date)}</span>
                                    {renderOpenOrderBtn(item)}
                                    {hasTransportPermission && (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); navigateToTransport(item); }}
                                        className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white flex items-center gap-1.5 transition-colors hover:opacity-90"
                                        style={{ backgroundColor: '#d4650a' }}
                                      >
                                        <ExternalLink className="w-3.5 h-3.5" />
                                        View on Transport
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      )
                    )}
                    
                    {/* Customer Results - Desktop - Directly after Transport */}
                    {hasCustomerPermission && filtered.customers.length > 0 && (activeTab === 'all' || activeTab === 'customers') && (
                      <div className="p-3">
                        <div className="flex items-center gap-2 mb-3 px-2">
                          <Users className="w-4 h-4 text-blue-400" />
                          <span className="text-sm font-medium text-blue-400">Customers ({filtered.customers.length})</span>
                        </div>
                        <div className="grid gap-2">
                          {filtered.customers.map((customer) => (
                            <div 
                              key={`customer-desktop-${customer.id}`}
                              className="rounded-lg overflow-hidden bg-white/5 hover:bg-white/10 border-l-2 border-blue-500 p-3 transition-all cursor-pointer"
                              onClick={() => {
                                setIsOpen(false);
                                navigate(`/customer-management?customer=${customer.id}`);
                              }}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-3">
                                    <Building2 className="w-4 h-4 text-blue-400" />
                                    <span className="font-semibold text-white">
                                      {customer.ledger_name || customer.account_name}
                                    </span>
                                    {customer.customer_type && (
                                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-600/30 text-blue-300">
                                        {customer.customer_type}
                                      </span>
                                    )}
                                  </div>
                                  {customer.ledger_name && customer.account_name !== customer.ledger_name && (
                                    <p className="text-xs text-gray-400 mt-1 ml-7">{customer.account_name}</p>
                                  )}
                                  <div className="flex items-center gap-6 mt-2 text-xs text-gray-400 ml-7">
                                    {customer.contact_name && (
                                      <span className="flex items-center gap-1.5">
                                        <Users className="w-3 h-3" />
                                        {customer.contact_name}
                                      </span>
                                    )}
                                    {(customer.phone || customer.contact_phone) && (
                                      <span className="flex items-center gap-1.5">
                                        <Phone className="w-3 h-3" />
                                        {customer.phone || customer.contact_phone}
                                      </span>
                                    )}
                                    {customer.billing_code && (
                                      <span className="flex items-center gap-1.5">
                                        <MapPin className="w-3 h-3" />
                                        {customer.billing_code}
                                      </span>
                                    )}
                                    {customer.billing_city && (
                                      <span className="text-gray-500">{customer.billing_city}</span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 ml-4">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setIsOpen(false);
                                      navigate(`/customer-management?customer=${customer.id}`);
                                    }}
                                    className="px-3 py-1.5 rounded text-xs font-semibold bg-blue-600 hover:bg-blue-500 text-white flex items-center gap-1 transition-colors"
                                  >
                                    <Eye className="w-3.5 h-3.5" />
                                    View Record
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setIsOpen(false); // Close search dropdown
                                      setSelectedCustomerForCall(customer);
                                      setShowCallNoteModal(true);
                                    }}
                                    className="px-3 py-1.5 rounded text-xs font-semibold text-white flex items-center gap-1 transition-colors hover:opacity-90"
                                    style={{ backgroundColor: enconOrange }}
                                  >
                                    <PhoneCall className="w-3.5 h-3.5" />
                                    Log Call
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Projects Results - Desktop */}
                    {hasProjectPermission && filtered.projects.length > 0 && (activeTab === 'all' || activeTab === 'projects') && (
                      <div className="p-3">
                        <div className="flex items-center gap-2 mb-3 px-2">
                          <FolderKanban className="w-4 h-4" style={{ color: enconOrange }} />
                          <span className="text-sm font-medium" style={{ color: enconOrange }}>Projects ({filtered.projects.length})</span>
                        </div>
                        <div className="grid gap-2">
                          {filtered.projects.map((project) => (
                            <div 
                              key={`project-desktop-${project.id}`}
                              className="rounded-lg overflow-hidden bg-white/5 hover:bg-white/10 border-l-2 p-3 transition-all"
                              style={{ borderLeftColor: enconOrange }}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-3 flex-wrap">
                                    <FolderKanban className="w-4 h-4" style={{ color: enconOrange }} />
                                    <span className="font-semibold text-white">{project.project_name || 'Unnamed Project'}</span>
                                    {project.category === 'key' && (
                                      <span className="px-2 py-0.5 rounded text-xs font-bold bg-yellow-600 text-white">
                                        Key Project
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                                    {project.city && (
                                      <span className="flex items-center gap-1.5">
                                        <MapPin className="w-3 h-3" />
                                        {project.city}{project.postcode ? `, ${project.postcode}` : ''}
                                      </span>
                                    )}
                                    {project.market_sector && (
                                      <span>{project.market_sector}</span>
                                    )}
                                    {project.encon_brand && (
                                      <span className="text-orange-300">{project.encon_brand}</span>
                                    )}
                                  </div>
                                  {/* Associated Customers Dropdown */}
                                  {project.sub_contractors && project.sub_contractors.length > 0 && (
                                    <div className="mt-2">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setExpandedProjectId(expandedProjectId === project.id ? null : project.id);
                                        }}
                                        className="flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300 transition-colors"
                                      >
                                        <Users className="w-3 h-3" />
                                        {project.sub_contractors.length} Associated Customer{project.sub_contractors.length !== 1 ? 's' : ''}
                                        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expandedProjectId === project.id ? 'rotate-180' : ''}`} />
                                      </button>
                                      {expandedProjectId === project.id && (
                                        <div className="mt-2 ml-4 space-y-1 animate-slideDown bg-white/5 rounded-lg p-2">
                                          {project.sub_contractors.map((sc, idx) => (
                                            <div 
                                              key={`sc-desktop-${project.id}-${idx}`}
                                              className="flex items-center justify-between text-xs text-gray-300 hover:text-white cursor-pointer py-1"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setIsOpen(false);
                                                navigate(`/customer-management?search=${encodeURIComponent(sc.customer_name || '')}`);
                                              }}
                                            >
                                              <span className="flex items-center gap-1.5">
                                                <Building2 className="w-3 h-3" />
                                                {sc.customer_name || 'Unknown'}
                                              </span>
                                              {sc.role && <span className="text-gray-500 text-[10px]">{sc.role}</span>}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                                <div className="flex flex-col items-end gap-2 ml-4">
                                  {project.project_value && (
                                    <span className="text-lg font-bold text-green-400">
                                      {formatProjectValue(project.project_value)}
                                    </span>
                                  )}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setIsOpen(false);
                                      navigate(`/projects?highlight=${project.id}`);
                                    }}
                                    className="px-3 py-1.5 rounded text-xs font-semibold text-white flex items-center gap-1 transition-colors hover:opacity-80"
                                    style={{ backgroundColor: enconOrange }}
                                  >
                                    <Eye className="w-3.5 h-3.5" />
                                    View Project
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* P.O. Expenses Results - Show in normal position if NOT shown first */}
                    {!showExpensesFirst && filtered.expenses.length > 0 && (activeTab === 'all' || activeTab === 'expenses') && (
                      <div className="p-3">
                        <div className="flex items-center gap-2 mb-3 px-2">
                          <Receipt className="w-4 h-4 text-blue-400" />
                          <span className="text-sm font-medium text-blue-400">P.O. Expenses ({filtered.expenses.length})</span>
                        </div>
                        <div className="grid gap-2">
                          {filtered.expenses.map((expense) => (
                            <div 
                              key={`exp-${expense.id}`}
                              className="rounded-lg overflow-hidden bg-white/5 hover:bg-white/10 border-l-2 border-blue-500 p-3 cursor-pointer transition-all"
                              onClick={() => handleExpenseClick(expense)}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-3 flex-wrap">
                                    <span className="px-2 py-0.5 rounded text-xs font-bold bg-blue-600 text-white">
                                      {expense.po_number}
                                    </span>
                                    <span className="font-semibold text-white">{expense.supplier || 'No Supplier'}</span>
                                    {expense.status === 'cancelled' && (
                                      <span className="px-2 py-0.5 rounded text-xs font-bold bg-red-500 text-white">
                                        Cancelled
                                      </span>
                                    )}
                                    {expense.linked_transfer_id && (
                                      <span className="px-2 py-0.5 rounded text-xs font-semibold bg-purple-600 text-white">
                                        Linked Transfer
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                                    <span>{expense.order_date}</span>
                                    {expense.po_type && <span className="capitalize">{expense.po_type.replace(/_/g, ' ')}</span>}
                                    {expense.m4_order_no && <span>M4: {expense.m4_order_no}</span>}
                                    {expense.ordered_by && <span>By: {expense.ordered_by}</span>}
                                  </div>
                                </div>
                                <div className="flex flex-col items-end gap-1 ml-4">
                                  <span className="text-lg font-bold text-green-400">£{(expense.total_cost || 0).toFixed(2)}</span>
                                  {expense.customer_recharge_amount > 0 && (
                                    <span className="text-xs text-blue-300">Recharge: £{expense.customer_recharge_amount.toFixed(2)}</span>
                                  )}
                                  <button
                                    className="px-3 py-1 rounded text-xs font-bold text-white flex items-center gap-1 mt-1"
                                    style={{ backgroundColor: enconOrange }}
                                    onClick={(e) => { e.stopPropagation(); handleExpenseClick(expense); }}
                                  >
                                    <ExternalLink className="w-3 h-3" />
                                    Open P.O.
                                  </button>
                                </div>
                              </div>
                              {expense.notes && (
                                <p className="text-xs text-gray-500 mt-2 truncate">{expense.notes}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Error Log Results - Desktop */}
                    {hasErrorLogPermission && filtered.errorLog.length > 0 && (activeTab === 'all' || activeTab === 'errorlog') && (
                      <div className="p-3">
                        <div className="flex items-center gap-2 mb-3 px-2">
                          <AlertTriangle className="w-4 h-4 text-red-400" />
                          <span className="text-sm font-medium text-red-400">Error Log ({filtered.errorLog.length})</span>
                        </div>
                        <div className="grid gap-2">
                          {filtered.errorLog.map((entry) => (
                            <div 
                              key={`errlog-desktop-${entry.id}`}
                              className="rounded-lg overflow-hidden bg-white/5 hover:bg-white/10 border-l-2 border-red-500 p-3 transition-all"
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-3 flex-wrap">
                                    <span className="px-2 py-0.5 rounded text-xs font-bold bg-red-600 text-white">
                                      #{entry.order_number}
                                    </span>
                                    <span className="font-semibold text-white">{entry.customer_name || 'Unknown Customer'}</span>
                                    <span className={`px-2 py-0.5 rounded text-xs font-bold text-white ${entry.status === 'resolved' ? 'bg-green-500' : 'bg-yellow-500'}`}>
                                      {entry.status || 'active'}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                                    <span>{entry.date}</span>
                                    {entry.error_types && entry.error_types.length > 0 && (
                                      <span className="text-red-300">{entry.error_types.join(', ')}</span>
                                    )}
                                    {entry.names && entry.names.length > 0 && (
                                      <span>People: {entry.names.join(', ')}</span>
                                    )}
                                  </div>
                                  {entry.what_happened && (
                                    <p className="text-xs text-gray-400 mt-2 line-clamp-2">{entry.what_happened}</p>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 ml-4">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleViewErrorLog(entry); }}
                                    className="px-3 py-1.5 rounded text-xs font-semibold bg-gray-600 hover:bg-gray-500 text-white flex items-center gap-1 transition-colors"
                                  >
                                    <Eye className="w-3.5 h-3.5" />
                                    View
                                  </button>
                                  <button
                                    data-errorlog-edit="true"
                                    onClick={(e) => { e.stopPropagation(); handleErrorLogEdit(entry); }}
                                    className="px-3 py-1.5 rounded text-xs font-semibold bg-red-600 hover:bg-red-500 text-white flex items-center gap-1 transition-colors"
                                  >
                                    <Edit className="w-3.5 h-3.5" />
                                    Edit
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Goods In Results with Expandable Items - Show at END if NOT numeric search */}
                    {!showGoodsInFirst && (upcomingGoodsIn.length > 0 || pastGoodsIn.length > 0) && (activeTab === 'all' || activeTab === 'goodsin') && (
                      <div className="p-3">
                        {/* Upcoming */}
                        {upcomingGoodsIn.length > 0 && (
                          <>
                            <div className="flex items-center gap-2 mb-3 px-2">
                              <Package className="w-4 h-4 text-green-400" />
                              <span className="text-sm font-medium text-green-400">Goods In - Upcoming ({upcomingGoodsIn.length})</span>
                            </div>
                            <div className="grid gap-2 mb-4">
                              {upcomingGoodsIn.map((item, idx) => (
                                <div key={`gi-up-${idx}`} className="rounded-lg overflow-hidden">
                                  <button
                                    onClick={() => handleGoodsInClick(item, `gi-up-${idx}`)}
                                    className={`w-full text-left p-3 transition-all border-l-2 border-green-500 ${
                                      expandedGoodsInId === `gi-up-${idx}` ? 'bg-white/15' : 'hover:bg-white/10'
                                    }`}
                                  >
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-3">
                                          <span className="px-2 py-0.5 rounded text-xs font-bold bg-green-600 text-white">
                                            #{item.order_number}
                                          </span>
                                          <span className="font-semibold text-white">{item.supplier_name}</span>
                                          {item.status && (
                                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold text-white ${
                                              item.status === 'complete' ? 'bg-green-500' : item.status === 'failed' ? 'bg-red-500' : ''
                                            }`}>
                                              {item.status === 'complete' ? '✓ Complete' : item.status === 'failed' ? '✗ Failed' : ''}
                                            </span>
                                          )}
                                          <ChevronDown className={`w-4 h-4 text-gray-400 ml-auto transition-transform ${expandedGoodsInId === `gi-up-${idx}` ? 'rotate-180' : ''}`} />
                                        </div>
                                      </div>
                                      <span className="text-green-300 text-sm font-medium ml-4">{formatTransportDate(item.date)}</span>
                                    </div>
                                  </button>
                                  {expandedGoodsInId === `gi-up-${idx}` && renderGoodsInDetails(item)}
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                        
                        {/* Past */}
                        {pastGoodsIn.length > 0 && (
                          <>
                            <div className="flex items-center gap-2 mb-3 px-2">
                              <Package className="w-4 h-4" style={{ color: enconOrange }} />
                              <span className="text-sm font-medium" style={{ color: enconOrange }}>Goods In - Past ({pastGoodsIn.length})</span>
                            </div>
                            <div className="grid gap-2">
                              {pastGoodsIn.map((item, idx) => (
                                <div key={`gi-past-${idx}`} className="rounded-lg overflow-hidden">
                                  <button
                                    onClick={() => handleGoodsInClick(item, `gi-past-${idx}`)}
                                    className={`w-full text-left p-3 transition-all border-l-2 ${
                                      expandedGoodsInId === `gi-past-${idx}` ? 'bg-white/15' : 'hover:bg-white/10'
                                    }`}
                                    style={{ borderLeftColor: enconOrange }}
                                  >
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-3">
                                          <span className="px-2 py-0.5 rounded text-xs font-bold text-white" style={{ backgroundColor: enconOrange }}>
                                            #{item.order_number}
                                          </span>
                                          <span className="font-semibold text-white">{item.supplier_name}</span>
                                          {item.status && (
                                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold text-white ${
                                              item.status === 'complete' ? 'bg-green-500' : item.status === 'failed' ? 'bg-red-500' : ''
                                            }`}>
                                              {item.status === 'complete' ? '✓ Complete' : item.status === 'failed' ? '✗ Failed' : ''}
                                            </span>
                                          )}
                                          <ChevronDown className={`w-4 h-4 text-gray-400 ml-auto transition-transform ${expandedGoodsInId === `gi-past-${idx}` ? 'rotate-180' : ''}`} />
                                        </div>
                                      </div>
                                      <span className="text-sm font-medium ml-4" style={{ color: '#ffb366' }}>{formatTransportDate(item.date)}</span>
                                    </div>
                                  </button>
                                  {expandedGoodsInId === `gi-past-${idx}` && renderGoodsInDetails(item)}
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
      
      {/* Customer / Project mode results dropdown — simplified single list,
          same slide-down position as the main dropdown above. */}
      {isOpen && searchQuery && searchMode !== 'global' && createPortal(
        <div
          ref={dropdownRef}
          className="fixed left-0 right-0 z-[60] animate-slideDown"
          style={{ top: `${dropdownTop}px` }}
          data-testid="global-search-mode-dropdown"
        >
          <div
            className="w-full"
            style={{
              background: 'rgba(45, 45, 45, 0.97)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              borderBottom: `2px solid ${searchMode === 'customer' ? 'rgba(238,114,3,0.5)' : 'rgba(13,148,136,0.5)'}`,
              boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            }}
          >
            <div className="px-4 lg:px-8 py-5 max-h-[60vh] overflow-y-auto">
              <div className="text-[11px] font-bold uppercase tracking-wide text-white/50 mb-3">
                {searchMode === 'customer' ? 'Customers' : 'Projects'}
              </div>
              {modeLoading && (
                <div className="text-center text-white/60 text-sm py-6">Searching…</div>
              )}
              {!modeLoading && modeResults.length === 0 && searchQuery.trim().length >= 2 && (
                <div className="text-center text-white/40 text-sm py-6">No matches found</div>
              )}
              {!modeLoading && modeResults.length > 0 && (
                <div className="space-y-1.5">
                  {modeResults.map((r) => (
                    <div
                      key={r.id}
                      className="w-full flex items-center gap-2 px-4 py-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors border border-white/10"
                    >
                      {searchMode === 'project' && !r.barbour_project_id && (
                        <div className="w-[118px] shrink-0" aria-hidden="true" />
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          if (searchMode === 'customer') {
                            setCustomerModalAccount(r);
                          } else {
                            setProjectModalId(r.id);
                          }
                          setIsOpen(false);
                          setSearchQuery('');
                          setSearchMode('global');
                        }}
                        className="flex-1 min-w-0 text-center"
                        data-testid={`global-search-mode-result-${r.id}`}
                      >
                        <div className="font-semibold text-sm text-white">
                          {searchMode === 'customer' ? r.name : (r.name || 'Unnamed Project')}
                        </div>
                        <div className="text-xs text-white/50 flex items-center justify-center gap-2 flex-wrap mt-0.5">
                          {searchMode === 'customer' && (
                            <>
                              {r.city && <span>{r.city}</span>}
                              {r.customer_code && <span>#{r.customer_code}</span>}
                            </>
                          )}
                          {searchMode === 'project' && (
                            <>
                              {r.account_name && <span>{r.account_name}</span>}
                              {r.town && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{[r.town, r.postcode].filter(Boolean).join(', ')}</span>}
                              {r.barbour_project_id && <span className="flex items-center gap-0.5"><Hash className="w-3 h-3" />{r.barbour_project_id}</span>}
                              {r._fromAccount && <span style={{ color: tealTeal }}>via {r._fromAccount}</span>}
                            </>
                          )}
                        </div>
                      </button>
                      {searchMode === 'project' && !r.barbour_project_id && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setAddBarbourFor({ id: r.id, name: r.name }); }}
                          className="w-[118px] shrink-0 flex items-center justify-center gap-1 text-[11px] font-bold px-2.5 py-1.5 rounded-lg transition-opacity hover:opacity-90 whitespace-nowrap text-white"
                          style={{ background: tealTeal }}
                          data-testid={`global-search-add-barbour-id-${r.id}`}
                        >
                          <Hash className="w-3 h-3" /> Add Barbour ID
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
      
      {/* Stock Details Modal */}
      {showStockModal && selectedStock && createPortal(
        <StockModal stock={selectedStock} onClose={() => setShowStockModal(false)} />,
        document.body
      )}
      
      {/* Goods In Edit Modal */}
      {showGoodsInEditModal && editingGoodsIn && (
        <GoodsInEditModal
          entry={editingGoodsIn}
          form={goodsInForm}
          setForm={setGoodsInForm}
          onSave={handleUpdateGoodsIn}
          onClose={() => { setShowGoodsInEditModal(false); setEditingGoodsIn(null); }}
        />
      )}
      
      {/* P.O. Expense View Modal - Using createPortal for proper z-index */}
      {showExpenseModal && selectedExpense && createPortal(
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-[9999]" 
            onClick={() => { setShowExpenseModal(false); setMatchInvoiceMode(false); }}
          />
          <div className="fixed inset-0 flex items-center justify-center z-[10000] pointer-events-none">
            <div 
              className="max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto pointer-events-auto"
              style={{
                background: 'white',
                borderRadius: '16px',
                border: '3px solid transparent',
                backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #ee7203, #ec4899)',
                backgroundOrigin: 'border-box',
                backgroundClip: 'padding-box, border-box',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
              }}
            >
              <div className="p-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl font-bold" style={{ color: enconOrange }}>{selectedExpense.po_number}</span>
                    {selectedExpense.status === 'cancelled' && (
                      <span className="text-sm px-2 py-1 bg-red-100 text-red-600 rounded">Cancelled</span>
                    )}
                    {selectedExpense.invoice_matched && (
                      <span className="text-sm px-2 py-1 bg-green-100 text-green-600 rounded flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" />
                        Matched
                      </span>
                    )}
                  </div>
                  <button 
                    onClick={() => { setShowExpenseModal(false); setMatchInvoiceMode(false); }}
                    className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
                
                <div className="space-y-4">
                  {/* Summary Info */}
                  <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                    <div>
                      <div className="text-xs text-gray-500">Supplier</div>
                      <div className="font-medium">{selectedExpense.supplier || '-'}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Date</div>
                      <div className="font-medium">{formatDateOrdinal(selectedExpense.order_date)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Ordered By</div>
                      <div className="font-medium">{selectedExpense.ordered_by || '-'}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Type</div>
                      <div className="font-medium">
                        {PO_TYPES.find(t => t.value === selectedExpense.po_type)?.label || selectedExpense.po_type}
                      </div>
                    </div>
                    {selectedExpense.customer_name && (
                      <div>
                        <div className="text-xs text-gray-500">Customer Name</div>
                        <div className="font-medium">{selectedExpense.customer_name}</div>
                      </div>
                    )}
                    {selectedExpense.delivery_postcode && (
                      <div>
                        <div className="text-xs text-gray-500">Delivery Postcode</div>
                        <div className="font-medium">{selectedExpense.delivery_postcode}</div>
                      </div>
                    )}
                    {selectedExpense.m4_order_no && (
                      <div>
                        <div className="text-xs text-gray-500">M4 Order No</div>
                        <div className="font-medium">{selectedExpense.m4_order_no}</div>
                      </div>
                    )}
                    {selectedExpense.linked_contribution > 0 && (
                      <div>
                        <div className="text-xs text-gray-500">Linked Contribution</div>
                        <div className="font-medium text-green-600">£{formatCurrency(selectedExpense.linked_contribution)}</div>
                      </div>
                    )}
                  </div>
                  
                  {/* Additional Customers Display */}
                  {selectedExpense.additional_customers && selectedExpense.additional_customers.length > 0 && (
                    <div className="bg-gray-50 p-3 rounded-lg border">
                      <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Additional Customers</div>
                      <div className="space-y-2">
                        {selectedExpense.additional_customers.map((cust, index) => (
                          <div key={index} className="flex items-center gap-4 text-sm bg-white p-2 rounded border">
                            <span className="font-medium text-gray-600">#{index + 2}</span>
                            {cust.customer_name && <span className="font-medium">{cust.customer_name}</span>}
                            {cust.m4_order_no && <span className="text-gray-500">Order: {cust.m4_order_no}</span>}
                            {cust.delivery_postcode && <span className="text-gray-500">{cust.delivery_postcode}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Cost Boxes */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-orange-50 p-4 rounded-lg border-2 text-center" style={{ borderColor: enconOrange }}>
                      <div className="text-xs text-gray-500">Cost</div>
                      <div className="font-bold text-2xl" style={{ color: enconOrange }}>
                        £{formatCurrency(selectedExpense.total_cost || 0)}
                      </div>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-400 text-center">
                      <div className="text-xs text-gray-500">Customer Recharge</div>
                      <div className="font-bold text-2xl text-blue-600">
                        £{formatCurrency(selectedExpense.customer_recharge_amount || 0)}
                      </div>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg border-2 border-purple-400 text-center">
                      <div className="text-xs text-gray-500">H/O Recharge</div>
                      <div className="font-bold text-2xl text-purple-600">
                        £{formatCurrency(selectedExpense.ho_recharge_amount || 0)}
                      </div>
                    </div>
                  </div>
                  
                  {/* Notes */}
                  {selectedExpense.notes && (
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <div className="text-xs text-gray-500 mb-1">Notes</div>
                      <div className="text-sm">{selectedExpense.notes}</div>
                    </div>
                  )}
                  
                  {/* Message to Supplier */}
                  {selectedExpense.message_to_supplier && (
                    <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                      <div className="text-xs font-medium mb-1" style={{ color: enconOrange }}>Message to Supplier</div>
                      <div className="text-sm text-gray-700">{selectedExpense.message_to_supplier}</div>
                    </div>
                  )}
                  
                  {/* Query Notes (existing) */}
                  {selectedExpense.query_notes && !matchInvoiceMode && (
                    <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                      <div className="text-xs text-yellow-700 mb-1">Query Notes</div>
                      <div className="text-sm text-yellow-800">{selectedExpense.query_notes}</div>
                    </div>
                  )}
                  
                  {/* Match Invoice Extended Section */}
                  {matchInvoiceMode && (
                    <div className="border-t pt-4 mt-4 space-y-4 bg-green-50 -mx-6 px-6 pb-4 -mb-4 rounded-b-lg">
                      <div className="flex items-center gap-2 text-green-700 font-semibold">
                        <CheckCircle className="w-5 h-5" />
                        Match Invoice
                      </div>
                      
                      <div>
                        <Label className="text-sm font-medium text-gray-700">Query Notes</Label>
                        <Textarea
                          value={queryNotes}
                          onChange={(e) => setQueryNotes(e.target.value)}
                          placeholder="Add any query notes or discrepancies here..."
                          className="mt-1 min-h-[80px] bg-white"
                        />
                      </div>
                      
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          onClick={() => { setMatchInvoiceMode(false); setQueryNotes(''); }}
                          className="text-gray-600"
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="outline"
                          onClick={saveQueryNotes}
                          className="border-yellow-400 text-yellow-700 hover:bg-yellow-50"
                        >
                          Save Notes Only
                        </Button>
                        <Button
                          onClick={markAsMatched}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Mark as Matched
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Actions */}
                {!matchInvoiceMode && (
                  <div className="flex justify-between items-center pt-4 border-t mt-4">
                    <div className="flex gap-2">
                      {selectedExpense.status === 'cancelled' ? (
                        <Button
                          variant="outline"
                          onClick={() => handleUncancelPO(selectedExpense)}
                          className="text-green-600 border-green-300 hover:bg-green-50"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Restore P.O.
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          onClick={() => handleCancelPO(selectedExpense)}
                          className="text-red-500 border-red-300 hover:bg-red-50"
                        >
                          Cancel P.O.
                        </Button>
                      )}
                    </div>
                    <div className="flex gap-2 flex-wrap justify-end">
                      <Button
                        variant="outline"
                        onClick={() => generatePOPDF(selectedExpense)}
                        className="text-gray-700 border-gray-300 hover:bg-gray-50"
                      >
                        <FileDown className="w-4 h-4 mr-1" />
                        Create PDF
                      </Button>
                      <Button
                        onClick={toggleMatchInvoiceMode}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Match Invoice
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => copyPONumber(selectedExpense.po_number)}
                        style={{ borderColor: enconOrange, color: enconOrange }}
                      >
                        <Copy className="w-4 h-4 mr-1" />
                        Copy
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => handleEditExpense(selectedExpense)}
                        style={{ borderColor: enconOrange, color: enconOrange }}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
      
      {/* P.O. Expense Edit Modal - Using createPortal for proper z-index */}
      {showExpenseEditModal && selectedExpense && createPortal(
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-[9999]" 
            onClick={() => setShowExpenseEditModal(false)}
          />
          <div className="fixed inset-0 flex items-center justify-center z-[10000] pointer-events-none">
            <div 
              className="max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto pointer-events-auto"
              style={{
                background: 'white',
                borderRadius: '16px',
                border: '3px solid transparent',
                backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #ee7203, #ec4899)',
                backgroundOrigin: 'border-box',
                backgroundClip: 'padding-box, border-box',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
              }}
            >
              <div className="p-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <span className="text-2xl font-bold" style={{ color: enconOrange }}>Edit {selectedExpense.po_number}</span>
                  <button 
                    onClick={() => setShowExpenseEditModal(false)}
                    className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
                
                <div className="space-y-4">
                  {/* Form Fields */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Supplier</Label>
                      <Input
                        value={expenseEditForm.supplier}
                        onChange={(e) => setExpenseEditForm({ ...expenseEditForm, supplier: e.target.value.toUpperCase() })}
                        placeholder="Supplier name"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Order Date</Label>
                      <Input
                        type="date"
                        value={expenseEditForm.order_date}
                        onChange={(e) => setExpenseEditForm({ ...expenseEditForm, order_date: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Ordered By</Label>
                      <Input
                        value={expenseEditForm.ordered_by}
                        onChange={(e) => setExpenseEditForm({ ...expenseEditForm, ordered_by: e.target.value.toUpperCase() })}
                        placeholder="Initials"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-700">P.O. Type</Label>
                      <select
                        value={expenseEditForm.po_type}
                        onChange={(e) => setExpenseEditForm({ ...expenseEditForm, po_type: e.target.value })}
                        className="mt-1 w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-sm"
                      >
                        {PO_TYPES.map(type => (
                          <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Customer Name</Label>
                      <Input
                        value={expenseEditForm.customer_name}
                        onChange={(e) => setExpenseEditForm({ ...expenseEditForm, customer_name: e.target.value.toUpperCase() })}
                        placeholder="Customer name"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Delivery Postcode</Label>
                      <Input
                        value={expenseEditForm.delivery_postcode}
                        onChange={(e) => setExpenseEditForm({ ...expenseEditForm, delivery_postcode: e.target.value.toUpperCase() })}
                        placeholder="Postcode"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-700">M4 Order No</Label>
                      <Input
                        value={expenseEditForm.m4_order_no}
                        onChange={(e) => setExpenseEditForm({ ...expenseEditForm, m4_order_no: e.target.value })}
                        placeholder="M4 order number"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Total Cost (£)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={expenseEditForm.total_cost}
                        onChange={(e) => setExpenseEditForm({ ...expenseEditForm, total_cost: parseFloat(e.target.value) || 0 })}
                        placeholder="0.00"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Customer Recharge (£)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={expenseEditForm.customer_recharge_amount}
                        onChange={(e) => setExpenseEditForm({ ...expenseEditForm, customer_recharge_amount: parseFloat(e.target.value) || 0 })}
                        placeholder="0.00"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-700">H.O. Recharge (£)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={expenseEditForm.ho_recharge_amount}
                        onChange={(e) => setExpenseEditForm({ ...expenseEditForm, ho_recharge_amount: parseFloat(e.target.value) || 0 })}
                        placeholder="0.00"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Notes</Label>
                    <Textarea
                      value={expenseEditForm.notes}
                      onChange={(e) => setExpenseEditForm({ ...expenseEditForm, notes: e.target.value })}
                      placeholder="Internal notes"
                      rows={2}
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Message to Supplier</Label>
                    <Textarea
                      value={expenseEditForm.message_to_supplier}
                      onChange={(e) => setExpenseEditForm({ ...expenseEditForm, message_to_supplier: e.target.value })}
                      placeholder="Message for supplier"
                      rows={2}
                      className="mt-1"
                    />
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-4 border-t">
                    <Button
                      variant="outline"
                      onClick={() => setShowExpenseEditModal(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSaveExpenseEdit}
                      disabled={savingExpenseEdit}
                      className="flex-1"
                      style={{ backgroundColor: enconOrange }}
                    >
                      {savingExpenseEdit ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
      
      {/* Error Log Edit Modal - Higher z-index to appear above search dropdown */}
      {showErrorLogModal && selectedErrorLog && createPortal(
        <>
          {/* Custom overlay with high z-index */}
          <div 
            className="fixed inset-0 bg-black/50 z-[9999]" 
            onClick={() => setShowErrorLogModal(false)}
          />
          <div className="fixed inset-0 flex items-center justify-center z-[10000] pointer-events-none" data-errorlog-modal="true">
            <div 
              className="max-w-lg w-full mx-4 max-h-[85vh] overflow-y-auto pointer-events-auto"
              style={{
                background: 'white',
                borderRadius: '16px',
                border: '3px solid transparent',
                backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #ef4444, #ec4899)',
                backgroundOrigin: 'border-box',
                backgroundClip: 'padding-box, border-box',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
              }}
            >
              <div className="p-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                    <span className="text-xl font-bold text-red-600">Edit Error Log #{selectedErrorLog.order_number}</span>
                  </div>
                  <button 
                    onClick={() => setShowErrorLogModal(false)}
                    className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
            
                {/* Form Content */}
                <div className="space-y-4">
                  {/* Order & Customer Info */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-gray-500">Order Number</Label>
                      <Input
                        value={errorLogForm.order_number || ''}
                        onChange={(e) => setErrorLogForm({...errorLogForm, order_number: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Customer Name</Label>
                      <Input
                        value={errorLogForm.customer_name || ''}
                        onChange={(e) => setErrorLogForm({...errorLogForm, customer_name: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                  </div>
              
                  {/* Date & Status */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-gray-500">Date</Label>
                      <Input
                        type="date"
                        value={errorLogForm.date || ''}
                        onChange={(e) => setErrorLogForm({...errorLogForm, date: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Status</Label>
                      <select
                        value={errorLogForm.status || 'active'}
                        onChange={(e) => setErrorLogForm({...errorLogForm, status: e.target.value})}
                        className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      >
                        <option value="active">Active</option>
                        <option value="resolved">Resolved</option>
                      </select>
                    </div>
                  </div>
              
                  {/* What Happened */}
                  <div>
                    <Label className="text-xs text-gray-500">What Happened</Label>
                    <textarea
                      value={errorLogForm.what_happened || ''}
                      onChange={(e) => setErrorLogForm({...errorLogForm, what_happened: e.target.value})}
                      rows={3}
                      className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none"
                      placeholder="Describe what happened..."
                    />
                  </div>
              
                  {/* Resolution */}
                  <div>
                    <Label className="text-xs text-gray-500">Resolution</Label>
                    <textarea
                      value={errorLogForm.resolution || ''}
                      onChange={(e) => setErrorLogForm({...errorLogForm, resolution: e.target.value})}
                      rows={2}
                      className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none"
                      placeholder="How was it resolved..."
                    />
                  </div>
              
                  {/* Cost Info (Read-only display) */}
                  {(selectedErrorLog.cost_to_company > 0 || selectedErrorLog.cost_recovered > 0) && (
                    <div className="grid grid-cols-2 gap-3 p-3 bg-red-50 rounded-lg">
                      <div>
                        <div className="text-xs text-red-600">Cost to Company</div>
                        <div className="font-bold text-red-700">£{(selectedErrorLog.cost_to_company || 0).toFixed(2)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-green-600">Cost Recovered</div>
                        <div className="font-bold text-green-700">£{(selectedErrorLog.cost_recovered || 0).toFixed(2)}</div>
                      </div>
                    </div>
                  )}
              
                  {/* Actions */}
                  <div className="flex justify-end gap-3 pt-2">
                    <Button
                      variant="outline"
                      onClick={() => setShowErrorLogModal(false)}
                      disabled={savingErrorLog}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSaveErrorLog}
                      disabled={savingErrorLog}
                      style={{ backgroundColor: '#ef4444', color: 'white' }}
                      className="flex items-center gap-2"
                    >
                      {savingErrorLog ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Check className="w-4 h-4" />
                      )}
                      Save Changes
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
      
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slideDown {
          animation: slideDown 0.25s ease-out forwards;
        }
      `}</style>

      {/* Call Note Modal - Desktop */}
      <CallNoteModal
        isOpen={showCallNoteModal}
        onClose={() => { setShowCallNoteModal(false); setSelectedCustomerForCall(null); }}
        customer={selectedCustomerForCall}
        showViewOption={true}
        onViewInCallsLog={(customer) => {
          setShowCallNoteModal(false);
          setIsOpen(false);
          navigate(`/customer-management?customer=${customer.id}&tab=calls`);
        }}
      />

      {/* Customer toggle result → existing Customer Management modal */}
      <ContactNotesTasksModal
        isOpen={!!customerModalAccount || !!customerModalSearch}
        onClose={() => { setCustomerModalAccount(null); setCustomerModalSearch(null); setCustomerModalOrder(null); }}
        initialAccount={customerModalAccount}
        initialSearch={customerModalAccount ? null : customerModalSearch}
        initialOrder={customerModalOrder}
      />

      {/* Project toggle result → new Project modal (reuses ProjectDetailPanel) */}
      <ProjectManagementModal
        isOpen={!!projectModalId}
        onClose={() => setProjectModalId(null)}
        initialProjectId={projectModalId}
        user={user}
      />

      {/* Missing-Barbour-ID quick-add, triggered from a project search result */}
      <AddBarbourIdModal
        isOpen={!!addBarbourFor}
        onClose={() => setAddBarbourFor(null)}
        projectId={addBarbourFor?.id}
        projectName={addBarbourFor?.name}
        onAdded={() => { setAddBarbourFor(null); if (searchQuery) runModeSearch(searchQuery); }}
      />
    </>
  );
};

// Stock Modal Component
const StockModal = ({ stock, onClose }) => {
  if (!stock) return null;
  
  return (
    <div 
      className="fixed inset-0 z-[300] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div 
          className="sticky top-0 px-6 py-4 border-b flex items-center justify-between rounded-t-2xl"
          style={{ background: enconOrange }}
        >
          <div className="flex items-center gap-3">
            <Package className="w-6 h-6 text-white" />
            <div>
              <h3 className="text-xl font-bold text-white">{stock.product_code}</h3>
              <p className="text-white/80 text-sm">{stock.prefix}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/20 transition-colors">
            <X className="w-6 h-6 text-white" />
          </button>
        </div>
        
        <div className="p-6 space-y-6">
          <div>
            <label className="text-sm font-medium text-gray-500">Description</label>
            <p className="text-lg font-medium text-gray-900 mt-1">{stock.description || 'No description'}</p>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 bg-green-50 rounded-xl">
              <label className="text-sm font-medium text-green-600">In Stock</label>
              <p className="text-2xl font-bold text-green-700 mt-1">
                {stock.on_hand_quantity || stock.on_hand_balance || 0}
                <span className="text-sm font-normal ml-2">{stock.stock_unit || 'units'}</span>
              </p>
            </div>
            
            <div className="p-4 bg-yellow-50 rounded-xl">
              <label className="text-sm font-medium text-yellow-600">On Order</label>
              <p className="text-2xl font-bold text-yellow-700 mt-1">
                {stock.on_order_quantity || stock.on_order || 0}
                <span className="text-sm font-normal ml-2">{stock.stock_unit || 'units'}</span>
              </p>
            </div>
            
            <div className="p-4 bg-blue-50 rounded-xl">
              <label className="text-sm font-medium text-blue-600">Total Stock</label>
              <p className="text-2xl font-bold text-blue-700 mt-1">
                {stock.free_stock || stock.on_hand_quantity || stock.on_hand_balance || 0}
                <span className="text-sm font-normal ml-2">{stock.stock_unit || 'units'}</span>
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            {stock.supplier_group && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <label className="text-xs font-medium text-gray-500">Supplier Group</label>
                <p className="text-sm font-semibold text-gray-900 mt-0.5">{stock.supplier_group}</p>
              </div>
            )}
            {stock.analysis_group && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <label className="text-xs font-medium text-gray-500">Analysis Group</label>
                <p className="text-sm font-semibold text-gray-900 mt-0.5">{stock.analysis_group}</p>
              </div>
            )}
            {stock.category && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <label className="text-xs font-medium text-gray-500">Category</label>
                <p className="text-sm font-semibold text-gray-900 mt-0.5">{stock.category}</p>
              </div>
            )}
            {stock.location && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <label className="text-xs font-medium text-gray-500">Location</label>
                <p className="text-sm font-semibold text-gray-900 mt-0.5">{stock.location}</p>
              </div>
            )}
          </div>
          
          {stock.last_updated && (
            <div className="text-center text-sm text-gray-500 pt-4 border-t">
              Last updated: {new Date(stock.last_updated).toLocaleString()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Goods In Edit Modal Component
const GoodsInEditModal = ({ entry, form, setForm, onSave, onClose }) => {
  return createPortal(
    <div 
      className="fixed inset-0 z-[300] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-2xl w-full max-w-md max-h-[85vh] overflow-y-auto shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div 
          className="sticky top-0 px-6 py-4 border-b flex items-center justify-between rounded-t-2xl"
          style={{ background: enconOrange }}
        >
          <div className="flex items-center gap-3">
            <Edit3 className="w-6 h-6 text-white" />
            <div>
              <h3 className="text-lg font-bold text-white">Quick Edit</h3>
              <p className="text-white/80 text-sm">#{entry.order_number}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/20 transition-colors">
            <X className="w-5 h-5 text-white" />
          </button>
        </div>
        
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-1 text-gray-700">Date</label>
            <input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="w-full px-4 py-3 rounded-lg border-2 focus:border-orange-400 focus:ring-orange-400 focus:outline-none"
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold mb-1 text-gray-700">Supplier Name *</label>
            <input
              type="text"
              value={form.supplier_name}
              onChange={(e) => setForm({ ...form, supplier_name: e.target.value })}
              placeholder="Enter supplier name"
              className="w-full px-4 py-3 rounded-lg border-2 focus:border-orange-400 focus:ring-orange-400 focus:outline-none"
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold mb-1 text-gray-700">Order Number *</label>
            <input
              type="text"
              value={form.order_number}
              onChange={(e) => setForm({ ...form, order_number: e.target.value })}
              placeholder="Enter order number"
              className="w-full px-4 py-3 rounded-lg border-2 focus:border-orange-400 focus:ring-orange-400 focus:outline-none"
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold mb-1 text-gray-700">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Add any notes..."
              rows={3}
              className="w-full px-4 py-3 rounded-lg border-2 focus:border-orange-400 focus:ring-orange-400 focus:outline-none resize-none"
            />
          </div>
          
          {entry.status && (
            <div className={`p-3 rounded-lg text-sm font-semibold text-center ${
              entry.status === 'complete' ? 'bg-green-100 text-green-700' :
              entry.status === 'failed' ? 'bg-red-100 text-red-700' : ''
            }`}>
              Status: {entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}
            </div>
          )}
          
          <div className="flex gap-3 pt-2">
            <button
              onClick={onClose}
              className="flex-1 py-3 rounded-lg border-2 border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onSave}
              className="flex-1 py-3 rounded-lg text-white font-semibold transition-colors"
              style={{ backgroundColor: enconOrange }}
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default GlobalSearch;
